import { CBLocalizedTextObject } from "cbcore-ts/compiledScripts/CBDataInterfaces"
import { UITextMeasurement } from "uicore-ts/compiledScripts/UITextMeasurement"
import {
    EXTEND,
    IF,
    IS,
    IS_NIL,
    IS_NOT,
    IS_UNDEFINED,
    MAKE_ID,
    nil,
    NO,
    UIKeyValueSorter,
    UIKeyValueSorterSortingInstruction,
    UIObject,
    UIRectangle,
    UIStringFilter,
    UITableView,
    UITextField,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    ValueOf,
    YES
} from "uicore-ts"
import { LanguageService } from "../../LanguageService"
import { CellView } from "../CellView"
import { ColumnCellDescriptor, ColumnView } from "../ColumnView"
import { RowView } from "../RowView"
import { CBCheckbox } from "./CBCheckbox"
import { CBTableHeaderView } from "./CBTableHeaderView"
import { CBDataViewRow, CBTableRowView, CBTableViewColumnSizes, rowStateOf } from "./CBTableRowView"

type StringOnly<T> = T extends string ? T : never;

const kHeaderFilterControlAllowance = 28

type UITableViewWithOptionalMeasuredRowsHeight = UITableView & {
    measuredRowsHeight?: (
        constrainingWidth: number,
        maximumHeight?: number
    ) => {
        height: number
        wasClamped: boolean
    }
}

export type CBDataViewColumnFilterDataType = "text" | "numeric" | "category" | "date"

export type CBDataViewColumnFilterOption = string | number | {
    value: string | number
    label: string
}

export type CBDataViewColumnFilterDescriptor<
    DataType extends object = object
> = {
    /**
     * Controls which filter editor is rendered in the column header.
     *
     * - text: single contains field
     * - numeric: inclusive min/max range
     * - category: dropdown when options are supplied, comma-separated field otherwise
     * - date: inclusive from/to date range
     */
    dataType: CBDataViewColumnFilterDataType
    /**
     * Optional editor hint for text filters. CBDataView itself treats unknown
     * controls as a regular text field; styled subclasses can render richer
     * controls while preserving the same "contains" criterion.
     */
    control?: "text" | "autocomplete"
    /**
     * Overrides the descriptor keyPath for local filtering. Useful for computed
     * columns where the displayed value is not the raw descriptor keyPath.
     */
    keyPath?: StringOnly<keyof DataType> | string
    /**
     * Key sent in cooperative backend requests. Defaults to keyPath, then the
     * descriptor keyPath.
     */
    backendKeyPath?: string
    /** Local value provider for computed columns. */
    valueGetter?: (dataItem: DataType) => string | number | boolean | Date | undefined | null
    /** Category options displayed in the header. */
    options?: CBDataViewColumnFilterOption[]
    /** Autocomplete options for subclasses that render an autocomplete text control. */
    autocompleteStrings?: string[] | (() => string[] | Promise<string[]>)
    usesMultiWordAndSearch?: boolean
    strictSelection?: boolean
    validatesAgainstAutocomplete?: boolean
    placeholder?: string
    fromPlaceholder?: string
    toPlaceholder?: string
}

export type CBDataViewColumnFilterCriterion = {
    columnIndex: number
    title?: string
    keyPath: string
    dataType: CBDataViewColumnFilterDataType
} & (
    {
        operator: "contains"
        value: string
    } |
    {
        operator: "range"
        min?: number
        max?: number
    } |
    {
        operator: "dateRange"
        from?: string
        to?: string
    } |
    {
        operator: "in"
        values: string[]
    }
)

export type CBDataViewCooperativeSortFilterRequest = {
    searchText: string
    sortingInstructions: UIKeyValueSorterSortingInstruction[]
    filterCriteria: CBDataViewColumnFilterCriterion[]
}

// Descriptor that determines the columns of the table.
// The keyPath is a string that points to the property of DataType that is to be shown in the cells of the table.
// If keyPath is missing, then there must be an updateCell function and sorting is not allowable.
export type CBDataViewCellDescriptor<
    DataType extends object = object,
    CustomViewType extends UIView = UIView
> = {
    // --- Common Properties (Always available) ---
    title?: string | CBLocalizedTextObject;
    
    titleLabelAlignment?: ValueOf<typeof UITextView.textAlignment>;
    cellLabelAlignment?: ValueOf<typeof UITextView.textAlignment>;
    /**
     * Semantic alignment shortcut applied to both header and body cells unless
     * the more specific titleLabelAlignment/cellLabelAlignment values are set.
     */
    alignment?: ValueOf<typeof UITextView.textAlignment>;

    /** Relative column width used by CBDataView's descriptor-derived layout. */
    weight?: number;
    /** Fixed column width used by CBDataView's descriptor-derived layout. */
    absoluteWidth?: number;
    /** Minimum readable column width. When omitted, CBDataView derives one from the header. */
    minimumWidth?: number;
    
    identifierKeyPath?: StringOnly<keyof DataType> | string;
    parentIdentifierKeyPath?: StringOnly<keyof DataType> | string;
    
    dataType?: keyof typeof UIKeyValueSorter.dataType;
    filter?: CBDataViewColumnFilterDescriptor<DataType>;
    // @ts-ignore
    initialOrderingState?: ValueOf<{
        "unordered": "unordered",
        "descending": "descending",
        "ascending": "ascending"
    }>;
    
    defaultTitle?: string;
    
    initCustomView?: () => CustomViewType;
    initCell?: (cellView: CBCellView<DataType, CustomViewType>) => void;
    
    buttonWasPressed?: (
        cellView: CBCellView<DataType, CustomViewType>,
        dataItem: DataType,
        event: MouseEvent | TouchEvent | KeyboardEvent
    ) => void;

    /**
     * Optional custom hover-tooltip text for this column's cells (shown via the
     * RH-styled tooltip by RHTableRowView). When omitted, the cell falls back to a
     * tooltip of its own formatted display text. Use "\n" to separate lines;
     * return "" for no tooltip.
     */
    tooltipText?: (dataItem: DataType) => string;

    /**
     * When true, this column is excluded from all rendering and layout. It still
     * participates in sorting via its orderingState and sortingInstructions callback.
     */
    hidden?: boolean;
    
    /**
     * Controls the position of this descriptor's sort instructions in the final
     * merged instruction list. Higher number = earlier (like z-index). Descriptors
     * with equal priority are ordered by activation time (most recently clicked first).
     * Defaults to 0.
     */
    sortPriority?: number;
    
    /**
     * When present, called to produce the sort instructions this descriptor contributes
     * to the final merged instruction list. Called regardless of allowSorting or hidden.
     *
     * @param ownState   - The current ordering state of this descriptor's column
     *                     ("unordered" | "descending" | "ascending").
     * @param allStates  - The current ordering states of all descriptors, indexed by
     *                     full descriptor index (parallel to the descriptors array).
     * @returns An array of sort instructions. Return [] to contribute nothing.
     *
     * When absent, the default behaviour applies: emit
     * [{ keyPath, dataType, direction: ownState }] when ownState is not "unordered",
     * otherwise [].
     */
    sortingInstructions?: (
        ownState: string,
        allStates: string[]
    ) => UIKeyValueSorterSortingInstruction[];
    
} &
    (
        {
            keyPath: StringOnly<keyof DataType> | string;
            allowSorting?: boolean;
            updateCell?: (cellView: CBCellView<DataType, CustomViewType>, dataItem: DataType) => void;
        }
        |
        {
            keyPath?: undefined | "";
            allowSorting?: false;
            updateCell: (cellView: CBCellView<DataType, CustomViewType>, dataItem: DataType) => void;
        }
        );

// This type and the following helper function are a hack to make the custom view type be inferred correctly
type CBCellView<
    DataType extends object,
    CustomViewType extends UIView
> = CustomViewType extends UIView
    ? CellView<DataType, CustomViewType> & { customView: CustomViewType }
    : CellView<DataType, CustomViewType>;

// Helper function for type inference. This is only needed when a custom view is used.
export const CBDataViewCellDescriptorWithObject = <
    DataType extends object,
    CustomViewType extends UIView = UIView
>(
    descriptor: CBDataViewCellDescriptor<DataType, CustomViewType>
): CBDataViewCellDescriptor<DataType, CustomViewType> |
    CBSelectionColumnDescriptor<
        DataType,
        CustomViewType extends (UIView & { selected: boolean }) ? CustomViewType : CBCheckbox
    > => {
    return descriptor
}

// Selection column descriptor. Can appear at any position in the descriptors array.
// Multiple selection column descriptors with different identifiers are supported.
export type CBSelectionColumnDescriptor<
    DataType extends object = object,
    CustomViewType extends UIView & { selected: boolean } = CBCheckbox
> = {
    type: "selection"
    /** Unique key used to namespace this selection set in the static store. */
    identifier: string
    /** If set, toggling also writes the boolean value back to dataItem[keyPath]. */
    keyPath?: StringOnly<keyof DataType> | string
    /** Title rendered next to the header checkbox. */
    title?: string
    /** Relative column width used by CBDataView's descriptor-derived layout. */
    weight?: number
    /** Fixed column width used by CBDataView's descriptor-derived layout. */
    absoluteWidth?: number
    /** Minimum readable column width. When omitted, CBDataView derives one from the header. */
    minimumWidth?: number
    /** Per-row label rendered next to the row checkbox. */
    rowTitle?: (dataItem: DataType) => string
    /** When provided, disables the checkbox for rows where this returns false. */
    enabled?: (dataItem: DataType) => boolean
    /**
     * Controls how many rows may be selected simultaneously.
     *
     * - `"multiple"` (default) — any number of rows may be independently selected.
     * - `"single"` — selecting a row deselects all other rows first.
     *
     * When `treeSelection` is also set, the effective semantics of `selectionMode`
     * change slightly — see `treeSelection` for details.
     */
    selectionMode?: "multiple" | "single"
    /**
     * Controls tree-aware selection behaviour. Only meaningful when the data view
     * is in tree mode (`isTreeView = YES` / `setTreeData` was called) and the
     * first descriptor supplies `identifierKeyPath` + `parentIdentifierKeyPath`.
     *
     * - `"independent"` (default) — each row is toggled independently; no
     *   ancestor/descendant relationship is considered. No indeterminate state.
     *
     * - `"single"` — CPV-style single-node selection:
     *     • Checking a node selects it and clears all its descendants from the
     *       store (a coarser selection supersedes finer ones).
     *     • Unchecking a directly-selected node removes it.
     *     • Clicking a node that is *indeterminate* (has selected descendants but
     *       is not itself selected) clears all its descendants.
     *     • Ancestor nodes of selected nodes render as indeterminate to indicate
     *       partial coverage.
     *     • `selectionMode` is effectively forced to `"single"` in this mode.
     *
     * - `"cascadeDown"` — folder-select style:
     *     • Checking a node adds it and all its descendants to the store.
     *     • Unchecking a node removes it and all its descendants.
     *     • Ancestor nodes render as indeterminate when only some descendants
     *       are selected (i.e. partial coverage above).
     *     • Compatible with `selectionMode: "multiple"` (default).
     *
     * - `"cascadeUp"` — bubble-up style:
     *     • Checking a node adds it; if all siblings are now selected the parent
     *       is also added, recursively up the tree.
     *     • Unchecking a node removes it and all its ancestors (a parent cannot
     *       remain selected if a child is deselected).
     *     • Ancestor nodes render as indeterminate when partially covered.
     *     • Compatible with `selectionMode: "multiple"` (default).
     */
    treeSelection?: "independent" | "single" | "cascadeDown" | "cascadeUp"
    /** Show a master checkbox in the header. Defaults to true. */
    masterSelectionEnabled?: boolean
    /** Supply a custom view that has a `selected` property. Defaults to CBCheckbox. */
    initCustomView?: () => CustomViewType
    /** Called once when the cell is created — for one-time appearance setup. */
    initCell?: (cellView: CellView<DataType, CustomViewType>) => void
    /** Called each time the cell is bound to data — for per-row appearance. */
    updateCell?: (cellView: CellView<DataType, CustomViewType>, dataItem: DataType) => void
}

/** Union of all column descriptor kinds accepted by CBDataView. */
export type CBColumnDescriptor<
    DataType extends object = object
> = CBDataViewCellDescriptor<DataType> | CBSelectionColumnDescriptor<DataType>

/** Type guard: returns true when the descriptor is a selection column. */
export function isCBSelectionColumnDescriptor<DataType extends object>(
    descriptor: CBColumnDescriptor<DataType>
): descriptor is CBSelectionColumnDescriptor<DataType> {
    return (descriptor as CBSelectionColumnDescriptor<DataType>).type === "selection"
}

// What a column cell holds, as the column filters compare it.
type CBColumnFilterValue = string | number | boolean | Date | null | undefined | object


export class CBDataView<DataType extends object = object> extends UIView {
    
    // -------------------------------------------------------------------------
    // Static selection store
    // Keyed by CBSelectionColumnDescriptor.identifier.
    // Maps identifier → Set of stable item tokens.
    // -------------------------------------------------------------------------
    
    static _selectionStore: Map<string, Set<string>> = new Map()
    static _itemTokens: WeakMap<object, string> = new WeakMap()
    
    /**
     * Global fallback class used to instantiate selection column checkboxes when
     * neither the descriptor's `initCustomView` nor the instance's
     * `selectionCheckboxClass` is set.
     *
     * Defaults to `CBCheckbox`. Override once at app startup to change the class
     * for every `CBDataView` in the application:
     *
     * ```ts
     * CBDataView.defaultSelectionCheckboxClass = RHCheckbox
     * ```
     */
    static defaultSelectionCheckboxClass: new () => CBCheckbox = CBCheckbox
    
    /**
     * Per-instance fallback class used to instantiate selection column checkboxes
     * when the descriptor's `initCustomView` is not set. Takes precedence over
     * `CBDataView.defaultSelectionCheckboxClass`.
     *
     * ```ts
     * this.myDataView.selectionCheckboxClass = RHCheckbox
     * ```
     */
    selectionCheckboxClass?: new () => CBCheckbox
    
    static _tokenForItem(item: object): string {
        let token = CBDataView._itemTokens.get(item)
        if (!token) {
            token = MAKE_ID()
            CBDataView._itemTokens.set(item, token)
        }
        return token
    }
    
    
    private _descriptors: CBDataViewCellDescriptor<DataType>[] = []
    // The raw mixed array as supplied by the caller, before selection descriptors are expanded.
    private _columnDescriptors: CBColumnDescriptor<DataType>[] = []
    
    /**
     * Per-identifier precomputed set of node IDs that are *ancestors* of at least
     * one currently-selected node. Rebuilt once per selection change (O(depth) per
     * selected node) and then checked O(1) per rendered row in updateCell.
     * Only populated when treeSelection !== "independent".
     */
    _selectionAncestorIds: Map<string, Set<string>> = new Map()
    
    private _filteringArray: string[] = []
    private _data: DataType[] = []
    
    highlightedDataItem?: DataType
    
    private _filteredData: CBDataViewRow<DataType>[] = []
    
    searchTextField: UITextField
    tableHeaderView: CBTableHeaderView<DataType>
    tableView: UITableView & CBTableViewColumnSizes
    _gridContainerView: ColumnView
    
    titleLabel: UITextView
    
    _noDataMessageLabel: UITextView
    _suppressHeightForRowWithIndexPerformanceWarning = NO
    _hasWarnedAboutUnboundedRowHeightEstimate = NO
    
    /** Hook called after search filtering updates the table. Used by CPVTreeDialogView. */
    didUpdateTableAfterSearchTextChange?: () => void

    /** Called after a tree expansion change has rebuilt the displayed rows. */
    displayedRowsDidChangeAfterTreeExpansion?: () => void
    
    /** Called whenever the selection for any selection column on this data view changes. */
    selectionDidChange?: (identifier: string) => void
    
    /**
     * Optional integration point for backend-assisted sorting/filtering.
     * CBDataView still applies local filtering and sorting; this callback exposes
     * the same request shape so a caller can later fetch server-filtered rows.
     */
    cooperativeSortFilterRequestDidChange?: (
        request: CBDataViewCooperativeSortFilterRequest
    ) => void | Promise<void>

    private _lastCooperativeSortFilterRequestJSON = ""
    
    /**
     * Set when the owner hands over `data` already filtered and sorted (for example paged from a server):
     * the data view then shows it exactly as given, with no local search, sorting or column filtering,
     * and skips the per-row indexing that only local filtering needs. Search and sort changes are still
     * reported through cooperativeSortFilterRequestDidChange for the owner to answer.
     */
    rowsAreInDisplayOrder = NO
    
    /**
     * Called with the first and last row index in or near the viewport (one viewport of padding) whenever
     * that range changes, and again after new data is shown. An owner holding only some of its rows loads
     * the missing ones here.
     */
    didChangeVisibleRowRange?: (firstRowIndex: number, lastRowIndex: number) => void
    
    _lastNotifiedVisibleRowRange?: { first: number, last: number }

    get noDataMessage(): string {
        return this._noDataMessageLabel.text
    }
    
    set noDataMessage(message: string) {
        this._noDataMessageLabel.text = message
        this._noDataMessageLabel.hidden = !message || this.filteredData.length > 0
        if (!this._noDataMessageLabel.hidden) {
            this._noDataMessageLabel.moveToTopOfSuperview()
        }
    }
    
    private _stringFilter = new UIStringFilter()
    private _stringSorter = new UIKeyValueSorter()
    _filterUpdateSequence = 0
    
    isSearchAvailable = YES
    performsSearchFilteringSynchronously = NO
    usesAutomaticColumnSizing = YES
    
    _highlightNextFilterChange = NO
    isTreeView = NO
    _rowToKeepStaticAfterUpdate?: CBTableRowView<DataType>
    
    private _hasResizableColumns = NO
    _resizingHandleViews: UIView[] = []
    _userRequestedWeights?: number[]
    
    // Row-height cache for variable-height tables. Keyed by row index; the
    // entire cache is discarded when the table width changes or data is replaced.
    // This prevents the repeated borrow-measure-return cycle during scrolling
    // that causes visible position jumps.
    _rowHeightCache = new Map<number, number>()
    _rowHeightCacheWidth = 0
    
    
    constructor(elementID?: string) {
        
        super(elementID)
        
        this.titleLabel = new UITextView(this.elementID + "TitleLabel", UITextView.type.header5)
        this.addSubview(this.titleLabel)
        
        
        this.searchTextField = new UITextField(this.elementID + "SearchTextField")
        this.searchTextField.style.padding = "5px"
        this.searchTextField.placeholderText = LanguageService.stringForKey(
            "CBTableViewSearch",
            LanguageService.currentLanguageKey,
            "Search"
        )
        this.addSubview(this.searchTextField)
        
        
        this.tableHeaderView = new CBTableHeaderView(this.elementID + "TableHeaderView")
        this._wireTableHeaderViewCallbacks(this.tableHeaderView)
        
        
        this.tableView = new UITableView(this.elementID + "TableView")
        this.tableView.allRowsHaveEqualHeight = NO
        
        // ColumnView wraps both the header and the table so the focus ring
        // encompasses both. This is the single tab stop for grid keyboard navigation.
        this._gridContainerView = new ColumnView(this.elementID + "GridContainer")
        this._gridContainerView.scrollsY = NO
        this._gridContainerView.viewHTMLElement.tabIndex = 0
        this.addSubview(this._gridContainerView)
        this._gridContainerView.descriptors = [
            ColumnCellDescriptor.intrinsic(this.tableHeaderView),
            ColumnCellDescriptor.scrollFill(this.tableView)
        ]
        
        // Tell the table to attach all keyboard listeners to the container element
        // instead of its own element, so the focus ring covers header + table.
        this.tableView._keyboardListenerElement = this._gridContainerView.viewHTMLElement
        // The table itself should not be a tab stop — the container is.
        this.tableView.viewHTMLElement.tabIndex = -1
        
        this._noDataMessageLabel = new UITextView(this.elementID + "NoDataMessageLabel")
        this._noDataMessageLabel.hidden = YES
        this._noDataMessageLabel.textAlignment = UITextView.textAlignment.center
        this.addSubview(this._noDataMessageLabel)
        
        this.updateHeaderShadow()
        this.tableView.configureWithObject({
            didScrollToPosition: EXTEND(() => {
                this.updateHeaderShadow()
                this.notifyVisibleRowRangeIfChanged()
            }),
            didLayoutSubviews: EXTEND(() => this.notifyVisibleRowRangeIfChanged())
        })
        
        this._setupKeyboardNavigationWiring()
        
        this.tableView.heightForRowWithIndex = index => {
            const tableWidth = this.tableView.frame.width || this.tableView.bounds.width
            
            // If the table width changed all previously measured heights are stale.
            if (tableWidth !== this._rowHeightCacheWidth) {
                this._rowHeightCache.clear()
                this._rowHeightCacheWidth = tableWidth
            }
            
            // When all rows have equal height only row 0 needs to be measured.
            // Normalise the index so every call hits the same cache entry and
            // the borrow-and-measure path below only ever runs once per width.
            const cacheIndex = this.tableView.allRowsHaveEqualHeight ? 0 : index
            
            const cached = this._rowHeightCache.get(cacheIndex)
            if (cached !== undefined) {
                return cached
            }
            
            const visibleRow = this.tableView.visibleRowWithIndex(cacheIndex)
            
            if (IS(visibleRow)) {
                const h = visibleRow.intrinsicContentHeight(tableWidth)
                this._rowHeightCache.set(cacheIndex, h)
                return h
            }
            
            // No visible row for this index — borrow one from the reuse pool (or allocate
            // a new one if the pool is empty), measure it, then immediately return it to
            // the pool. This guarantees the view is always tracked and prevents both
            // allocation churn and pool leaks.
            const borrowedRow = this.tableView.viewForRowWithIndex(cacheIndex)
            // The borrowed row is detached (no superview), so its cellWeights getter
            // falls back to equal weights instead of reading from the table. Seed the
            // stored weights explicitly so the virtual layout pass uses the same column
            // proportions as the live table and produces the correct row height.
            const tableWeights = this.tableView.cellWeights
            if (tableWeights?.length) {
                ;(borrowedRow as CBTableRowView).cellWeights = tableWeights
            }
            const tableWidths = this.tableView.cellWidths
            if (tableWidths?.length) {
                ;(borrowedRow as CBTableRowView).cellWidths = tableWidths
            }
            const tableMinimumWidths = this.tableView.cellMinimumWidths
            if (tableMinimumWidths?.length) {
                ;(borrowedRow as CBTableRowView).cellMinimumWidths = tableMinimumWidths
            }
            const result = borrowedRow.intrinsicContentHeight(tableWidth)
            this.tableView._markReusableViewAsUnused(borrowedRow)
            this._rowHeightCache.set(cacheIndex, result)
            return result
            
        }
        
        
        this.tableView.numberOfRows = () => this.filteredData.length
        
        // let firstRowView: CBTableRowView
        this.tableView.newReusableViewForIdentifier = (_identifier, rowIDIndex) => {
            
            const rowView = new CBTableRowView(
                this.elementID + "TableRow" + rowIDIndex
            )
            
            
            rowView.expandedValueDidChange = async (_row: UIView, event: MouseEvent) => {
                
                // @ts-ignore
                this.rowExpandedValueDidChange(rowView, event)
                
                // @ts-ignore
                this.keepRowStaticDuringNextUpdate(rowView)
                
                await this.updateTableDataByFiltering()
                
                this.setNeedsLayoutUpToRootView()
                this.displayedRowsDidChangeAfterTreeExpansion?.()
                
            }
            
            return rowView
            
        }
        
        this.tableView.viewForRowWithIndex = (index) => {
            
            const row = this.tableView.reusableViewForIdentifier("TableRow", index) as CBTableRowView<DataType>
            row.intrinsicHeightDidChange = changedRow => {
                const changedIndex = changedRow._UITableViewRowIndex ?? index
                const cacheKey = this.tableView.allRowsHaveEqualHeight ? 0 : changedIndex
                this._rowHeightCache.delete(cacheKey)
                this.tableView.invalidateSizeOfRowWithIndex(changedIndex)
                this.tableView.setNeedsLayout()
            }
            
            if (!row.descriptors.isEqualToArray(this._descriptors)) {
                row.descriptors = this._descriptors
            }
            
            row.data = this.filteredData[index]
            
            // Pre-seed _lastReportedHeight with the height the table is about to
            // assign this row. This prevents didLayoutSubviews from firing
            // spuriously on the first real layout pass when the height hasn't
            // actually changed from what the table measured.
            // When allRowsHaveEqualHeight is set, UITableView only measures row 0
            // and reuses that height for every row — so we use the row-0 cache
            // entry as the pre-seed for all rows.
            const cacheKey = this.tableView.allRowsHaveEqualHeight ? 0 : index
            const cachedHeight = this._rowHeightCache.get(cacheKey)
            if (cachedHeight !== undefined) {
                row._lastReportedHeight = cachedHeight
            }
            
            if (index % 2 == 0) {
                row.viewHTMLElement.classList.add("even")
                row.viewHTMLElement.classList.remove("odd")
            }
            else {
                row.viewHTMLElement.classList.add("odd")
                row.viewHTMLElement.classList.remove("even")
            }
            
            row.highlighted = IS(this.highlightedDataItem) && row.data == this.highlightedDataItem
            
            return row
            
        }
        
        
        this.searchTextField.addTargetForControlEvent(
            UITextField.controlEvent.TextChange,
            () => {
                
                if (this.tableView.isMemberOfViewTree) {
                    
                    // noinspection JSIgnoredPromiseFromCall
                    this.updateTableDataByFiltering().then(didApplyUpdate => {
                        if (didApplyUpdate) {
                            this.didUpdateTableAfterSearchTextChange?.()
                        }
                    })
                    
                }
                
            }
        )
        
    }
    
    
    protected _wireTableHeaderViewCallbacks(tableHeaderView: CBTableHeaderView<DataType>) {
        tableHeaderView.orderingStateDidChange = this.sortTableData.bind(this)
        tableHeaderView.columnFilterCriteriaDidChange = () => {
            if (this.tableView.isMemberOfViewTree) {
                this.updateTableDataByFiltering().then(didApplyUpdate => {
                    if (didApplyUpdate) {
                        this.didUpdateTableAfterSearchTextChange?.()
                    }
                })
            }
        }
        tableHeaderView.filterPanelExpansionDidChange = () => {
            this._gridContainerView.setNeedsLayout()
            this.tableView.setNeedsLayoutUpToRootView()
            this.layoutNoDataMessageIfAvailable()
            this.setNeedsLayoutUpToRootView()
        }
    }


    // Defined by subclasses that show a no-data message.
    layoutNoDataMessageIfPossible?(): void

    protected layoutNoDataMessageIfAvailable() {
        this.layoutNoDataMessageIfPossible?.()
    }


    // -------------------------------------------------------------------------
    // Keyboard navigation wiring
    // -------------------------------------------------------------------------
    
    /**
     * Wires UITableView keyboard navigation callbacks to this data view's
     * header and row views. Called once from the constructor.
     */
    _setupKeyboardNavigationWiring() {
        
        // When focus changes in the table, forward the highlight to the right row/header.
        this.tableView.keyboardFocusDidChange = (rowIndex, cellIndex) => {
            
            if (rowIndex === -1) {
                // Header row is focused
                this.tableHeaderView.setKeyboardFocusedCellIndex(cellIndex)
            }
            else if (rowIndex === undefined) {
                // Focus cleared — clear header too
                this.tableHeaderView.setKeyboardFocusedCellIndex(undefined)
            }
            else {
                // Data row — clear header highlight
                this.tableHeaderView.setKeyboardFocusedCellIndex(undefined)
            }
            
        }
        
        // When Enter is pressed, activate the focused cell.
        this.tableView.keyboardDidActivateCell = (rowIndex, cellIndex) => {
            
            if (rowIndex === -1) {
                // Activate a header column (trigger sort)
                this.tableHeaderView.activateFocusedCell()
                return
            }
            
            const row = this.tableView.visibleRowWithIndex(rowIndex) as CBTableRowView<DataType> | undefined
            if (!row) {
                return
            }
            
            const cell = row.dataCells[cellIndex]
            if (!cell) {
                return
            }
            
            if (cell.isAButton) {
                cell.sendControlEventForKey("PointerUpInside", nil)
            }
            
        }
        
    }
    
    /**
     * Focuses the table and moves keyboard highlight to the first row, first cell.
     * Call this from viewDidAppear to give the table focus without requiring a click.
     */
    focusTable(rowIndex = 0, cellIndex = 0) {
        if (this.filteredData.length > 0) {
            this.tableView.focusRowAtIndex(rowIndex, cellIndex)
        }
    }
    
    
    override set hidden(hidden: boolean) {
        
        super.hidden = hidden
        if (hidden) {
            this.searchTextField.removeFromSuperview()
            this.style.display = "none"
        }
        else {
            this.searchTextField.addedAsSubviewToView(this)
            this.style.display = ""
        }
        
    }
    
    override get hidden(): boolean {
        return super.hidden
    }
    
    notifyVisibleRowRangeIfChanged() {
        
        if (!this.didChangeVisibleRowRange || this.isVirtualLayouting) {
            return
        }
        
        // New rows reach the table asynchronously, so an empty table has no range yet.
        if (this.tableView.bounds.height <= 0 || this.tableView.numberOfRows() === 0) {
            return
        }
        
        const visibleIndexes = this.tableView.indexesForVisibleRows(1)
        if (visibleIndexes.length === 0) {
            return
        }
        const range = { first: visibleIndexes.min(), last: visibleIndexes.max() }
        if (this._lastNotifiedVisibleRowRange?.first === range.first &&
            this._lastNotifiedVisibleRowRange?.last === range.last) {
            return
        }
        this._lastNotifiedVisibleRowRange = range
        this.didChangeVisibleRowRange(range.first, range.last)
        
    }
    
    private updateHeaderShadow() {
        
        const boxShadowString = "#0000005e 0px 2px 2px -2px"
        
        if (this.tableView.contentOffset.y > 0 && this.tableHeaderView.style.boxShadow != boxShadowString) {
            
            this.tableHeaderView.moveToTopOfSuperview()
            this.tableHeaderView.style.boxShadow = boxShadowString
            
        }
        else {
            
            this.tableHeaderView.style.boxShadow = ""
            
        }
        
    }
    
    override wasAddedToViewTree() {
        
        super.wasAddedToViewTree()
        
        this.updateTableDataByFiltering().then(nil)
        
        // If the table had a focused row before being removed from the tree
        // (e.g. navigating to a detail view and back), restore DOM focus so
        // arrow keys work immediately without requiring a click.
        if (this.tableView._keyboardFocusedRowIndex !== undefined) {
            this._gridContainerView.viewHTMLElement.focus({ preventScroll: true })
        }
        
    }
    
    
    override didReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        
        super.didReceiveBroadcastEvent(event)
        
        if (event.name == UIView.broadcastEventName.LanguageChanged) {
            
            this.tableView.reloadData()
            
        }
        
        
    }
    
    
    rowExpandedValueDidChange(row: CBTableRowView<DataType>, event: MouseEvent) {
        
        if (!this.isTreeView || !row.data) {
            return
        }
        
        const descriptor = this._descriptors.firstElement
        const identifierKeyPath = descriptor?.identifierKeyPath as string ?? "_id"
        const parentIdentifierKeyPath = descriptor?.parentIdentifierKeyPath as string ?? "_parent"
        const data = row.data
        if (!data) {
            return
        }
        const isNowExpanded = data._CB_DataView_IsExpanded
        const searchIsActive = this.searchTextField.text.trim().length > 0
        
        if (isNowExpanded) {
            
            // User manually opened this node.
            // Mark it as explicitly open and propagate open intent up to
            // the nearest ancestor that already has a manual state set.
            data._CB_DataView_ManualExpansion = "open"
            
            let parentKey = UIObject.valueForKeyPath(parentIdentifierKeyPath, data) as string
            while (parentKey) {
                const parentItem = this._dataByIdentifier.get(parentKey)
                if (!parentItem) {
                    break
                }
                const parent = rowStateOf(parentItem)
                if (parent._CB_DataView_ManualExpansion !== undefined) {
                    break
                }
                parent._CB_DataView_ManualExpansion = "open"
                parentKey = UIObject.valueForKeyPath(parentIdentifierKeyPath, parent) as string
            }
            
        }
        else {
            
            // User manually closed this node.
            // Only record "closed" intent when the system wants it open (i.e. search is
            // active). Without a search there is no conflict, so we clear the intent
            // instead of recording a negative — keeping the state clean.
            data._CB_DataView_ManualExpansion = searchIsActive ? "closed" : undefined
            
        }
        
    }
    
    
    /**
     * Defines the columns of the table. Each descriptor maps a `keyPath` on the
     * data item to a visible column. Use `CBDataViewCellDescriptorWithObject` for
     * type inference when a custom view is involved.
     *
     * Basic column:
     * ```ts
     * CBDataViewCellDescriptorWithObject({
     *     title: "Name",
     *     keyPath: "name",
     *     weight: 2,
     *     alignment: UITextView.textAlignment.left,
     *     allowSorting: YES,
     *     updateCell: (cell, item) => { cell.text = item.name }
     * })
     * ```
     *
     * Column layout is descriptor-owned: visible descriptors contribute their
     * `weight` or `absoluteWidth` sequentially to the header and table rows.
     * Hidden descriptors are excluded from rendering and layout.
     *
     * Hidden sort-only column — participates in sorting but renders nothing.
     * `sortPriority` (higher = first) controls ordering relative to other columns.
     * `sortingInstructions` replaces the default single-instruction behaviour;
     * return `[]` to contribute nothing, or multiple instructions for compound sorts.
     * The callback fires on every sort pass regardless of `hidden` or `allowSorting`:
     * ```ts
     * CBDataViewCellDescriptorWithObject({
     *     keyPath: "isPinned",
     *     hidden: YES,
     *     allowSorting: YES,
     *     sortPriority: 1,
     *     sortingInstructions: () => [{ keyPath: "isPinned", dataType: "string", direction: "descending" }]
     * })
     * ```
     */
    get descriptors() {
        return this._descriptors
    }
    
    set descriptors(descriptors: CBColumnDescriptor<DataType>[]) {
        
        this._columnDescriptors = descriptors
        this._descriptors = this._expandedCellDescriptors(descriptors)
        const layoutParameters = this._layoutParametersForActiveDescriptors()
        
        // Invalidate the shared row height cache — a different descriptor set can
        // produce a different row height, so the previous measurement is stale.
        UIView.invalidateSharedIntrinsicSizeCache(this.tableView._equalRowHeightCacheIdentifier)
        
        this.tableHeaderView.descriptors = this._descriptors
        this.tableHeaderView.selectionColumnIndices = this._selectionColumnIndicesMap(descriptors)
        this._applyDescriptorLayoutParameters(layoutParameters)
        this.tableView._unusedReusableViews = {}
        
        // Sync ARIA column count with the table
        this.tableView.setColumnCount(layoutParameters.cellWeights.length)
        
        this.updateFilteringArray()
        this.updateTableDataByFiltering().then(nil)
        
        // Refresh the resizable columns property
        // noinspection SillyAssignmentJS
        this.hasResizableColumns = this.hasResizableColumns
        
    }
    
    
    set data(data) {
        
        // Preserve selections — only retain tokens whose item still exists in the new data.
        const selectionDescriptors = this._columnDescriptors.filter(isCBSelectionColumnDescriptor)
        const newTokens = new Set<string>()
        if (selectionDescriptors.length > 0) {
            data.forEach(item => newTokens.add(CBDataView._tokenForItem(item as object)))
        }
        for (const selDesc of selectionDescriptors) {
            const store = CBDataView._selectionStore.get(selDesc.identifier)
            if (store) {
                for (const token of Array.from(store)) {
                    if (!newTokens.has(token)) {
                        store.delete(token)
                    }
                }
            }
        }
        
        this._data = data
        
        // Rebuild the identifier→object map used for O(1) parent lookups in tree views
        const identifierKeyPath = this._descriptors.firstElement?.identifierKeyPath as string ?? "_id"
        this._dataByIdentifier = new Map(
            data.map((item): [string, DataType] => [UIObject.valueForKeyPath(identifierKeyPath, item) as string, item])
        )
        
        // Rebuild ancestor ID sets for any tree-selection columns.
        for (const descriptor of this._columnDescriptors) {
            if (isCBSelectionColumnDescriptor(
                descriptor) && descriptor.treeSelection && descriptor.treeSelection !== "independent") {
                this._rebuildSelectionAncestorIds(descriptor.identifier)
            }
        }
        
        // Form array of strings for filtering
        if (NO == this.rowsAreInDisplayOrder) {
            this.updateFilteringArray()
        }
        this.updateTableDataByFiltering().then(() => {
                this.clearIntrinsicSizeCache()
                this.setNeedsLayoutUpToRootView()
            }
        )
        this.clearIntrinsicSizeCache()
        this.setNeedsLayoutUpToRootView()
        
    }
    
    get data() {
        return this._data
    }
    
    
    setTreeData(data: DataType[], subElementsKeyPath: keyof DataType & string, expanded = YES) {
        
        // Flatten the tree
        
        const descriptor = this._descriptors.firstElement
        const identifierKeyPath = descriptor.identifierKeyPath as string ?? "_id"
        const parentIdentifierKeyPath = descriptor.parentIdentifierKeyPath as string ?? "_parent"
        
        
        const flatData: DataType[] = []
        
        
        function flattenData(data: DataType[], parentElementID?: string) {
            
            data.forEach(value => {
                
                flatData.push(value)
                
                let identifier = UIObject.valueForKeyPath(identifierKeyPath, value)
                if (IS_UNDEFINED(identifier) || IS_NIL(identifier)) {
                    identifier = MAKE_ID()
                    UIObject.setValueForKeyPath(identifierKeyPath, identifier, value, YES)
                }
                
                if (parentElementID?.length) {
                    UIObject.setValueForKeyPath(parentIdentifierKeyPath, parentElementID, value, YES)
                }
                
                const subElements: DataType[] | undefined = UIObject.valueForKeyPath(subElementsKeyPath, value)
                if (subElements?.length) {
                    
                    // @ts-ignore
                    if (!value._CB_DataView_IsExpandable) {
                        
                        // @ts-ignore
                        value._CB_DataView_IsExpandable = YES
                        // @ts-ignore
                        value._CB_DataView_IsExpanded = expanded
                        
                    }
                    
                    flattenData(subElements, identifier)
                    
                }
                
            })
            
        }
        
        flattenData(data)
        
        this.isTreeView = YES
        
        const groupedData = (data || []).groupedBy(item =>
            UIObject.valueForKeyPath(identifierKeyPath, item)
        )
        flatData.forEach(value => {
                
                const isExpanded = groupedData[UIObject.valueForKeyPath(
                    identifierKeyPath,
                    value
                    // @ts-ignore
                )]?.firstElement?._CB_DataView_IsExpanded
                
                if (isExpanded) {
                    // @ts-ignore
                    value._CB_DataView_IsExpanded = isExpanded
                }
                
            }
        )
        
        // Set data
        this.data = flatData
        
        
    }
    
    
    private updateFilteringArray() {
        
        if (!this._descriptors.length) {
            return
        }
        
        let data = this.data
        
        // // Only filtering root level rows
        // if (this.descriptors?.firstElement?.parentIdentifierKeyPath) {
        //     data = data.filter(
        //         value => IS_NOT(
        //             UIObject.valueForKeyPath(this.descriptors.firstElement.parentIdentifierKeyPath as string, value)
        //         )
        //     )
        // }
        
        this._filteringArray = this._searchStringsForData(data)
        
    }
    
    
    get filteredData(): CBDataViewRow<DataType>[] {
        return this._filteredData
    }
    
    set filteredData(value: CBDataViewRow<DataType>[]) {
        
        this._filteredData = value
        // The rows behind the same indexes may be different ones now, so the owner hears about the range again.
        this._lastNotifiedVisibleRowRange = undefined
        
        this._noDataMessageLabel.hidden = !this._noDataMessageLabel.text || value.length > 0
        this.tableView.hidden = value.length === 0
        if (!this._noDataMessageLabel.hidden) {
            this._noDataMessageLabel.moveToTopOfSuperview()
        }
        
        // Sync ARIA row count with actual visible row count
        this.tableView.setRowCount(value.length)
        
        this._rowHeightCache.clear()
        this.tableView.reloadData()
        this.setNeedsLayoutUpToRootView()
        this.tableView.invalidateSizeOfRowWithIndex(0)
        this.tableView.setNeedsLayoutUpToRootView()
        
        // This hack makes the resize handles go to the correct places, but is probably bad for performance and
        // allows for the bad layout to flicker on screen
        // UIView.layoutViewsIfNeeded()
        this.setNeedsLayout()
        
    }
    

    get cooperativeSortFilterRequest(): CBDataViewCooperativeSortFilterRequest {
        return {
            searchText: this.searchTextField.text,
            sortingInstructions: this.tableHeaderView.sortingInstructions,
            filterCriteria: this.tableHeaderView.columnFilterCriteria
        }
    }


    private _publishCooperativeSortFilterRequestIfNeeded() {
        if (!this.cooperativeSortFilterRequestDidChange) {
            return
        }
        const request = this.cooperativeSortFilterRequest
        const requestJSON = JSON.stringify(request)
        if (requestJSON === this._lastCooperativeSortFilterRequestJSON) {
            return
        }
        this._lastCooperativeSortFilterRequestJSON = requestJSON
        this.cooperativeSortFilterRequestDidChange(request)
    }

    keepRowStaticDuringNextUpdate(rowView: CBTableRowView<DataType>) {
        
        this._rowToKeepStaticAfterUpdate = rowView
        
    }
    
    updateRowWithNestingDepth(nestingDepth: number, rowView: CBTableRowView<DataType>) {
    
    
    }
    
    
    // Lazily-built lookup map for O(1) parent resolution in tree views.
    // Rebuilt whenever this.data is set (via updateFilteringArray).
    _dataByIdentifier: Map<string, DataType> = new Map()
    

    private _valueForColumnFilter(
        dataItem: DataType,
        descriptor: CBDataViewCellDescriptor<DataType>
    ): CBColumnFilterValue {
        const filterDescriptor = descriptor.filter
        if (filterDescriptor?.valueGetter) {
            return filterDescriptor.valueGetter(dataItem)
        }
        const keyPath = filterDescriptor?.keyPath ?? descriptor.keyPath
        if (!keyPath) {
            return undefined
        }
        return UIObject.valueForKeyPath(keyPath as string, dataItem)
    }


    private _dataItemMatchesColumnFilter(
        dataItem: DataType,
        criterion: CBDataViewColumnFilterCriterion
    ): boolean {
        const descriptor = this._descriptors[criterion.columnIndex]
        if (!descriptor) {
            return true
        }

        const rawValue = this._valueForColumnFilter(dataItem, descriptor)

        if (criterion.operator === "contains") {
            return (rawValue ?? "")
                .toString()
                .toLocaleLowerCase()
                .includes(criterion.value.toLocaleLowerCase())
        }

        if (criterion.operator === "range") {
            const numberValue = typeof rawValue === "number"
                                ? rawValue
                                : parseFloat((rawValue ?? "").toString().replace(",", "."))
            if (!Number.isFinite(numberValue)) {
                return false
            }
            if (criterion.min !== undefined && numberValue < criterion.min) {
                return false
            }
            if (criterion.max !== undefined && numberValue > criterion.max) {
                return false
            }
            return true
        }

        if (criterion.operator === "dateRange") {
            const dateValue = this._dateFromColumnFilterValue(rawValue)
            if (!dateValue) {
                return false
            }
            if (criterion.from) {
                const fromDate = this._dateFromColumnFilterValue(criterion.from)
                if (fromDate && dateValue < fromDate) {
                    return false
                }
            }
            if (criterion.to) {
                const toDate = this._dateFromColumnFilterValue(criterion.to)
                if (toDate) {
                    toDate.setHours(23, 59, 59, 999)
                    if (dateValue > toDate) {
                        return false
                    }
                }
            }
            return true
        }

        const comparableValue = (rawValue ?? "").toString()
        return criterion.values.includes(comparableValue)
    }


    private _dateFromColumnFilterValue(value: CBColumnFilterValue): Date | undefined {
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) {
                return undefined
            }
            return value
        }
        if (typeof value === "number") {
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) {
                return undefined
            }
            return date
        }
        const textValue = (value ?? "").toString().trim()
        if (!textValue.length) {
            return undefined
        }
        const dateOnlyMatch = textValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (dateOnlyMatch) {
            const dateOnlyValue = new Date(
                Number.parseInt(dateOnlyMatch[1], 10),
                Number.parseInt(dateOnlyMatch[2], 10) - 1,
                Number.parseInt(dateOnlyMatch[3], 10)
            )
            if (Number.isNaN(dateOnlyValue.getTime())) {
                return undefined
            }
            return dateOnlyValue
        }
        const date = new Date(textValue)
        if (Number.isNaN(date.getTime())) {
            return undefined
        }
        return date
    }


    private _dataItemMatchesColumnFilters(dataItem: DataType): boolean {
        const filterCriteria = this.tableHeaderView.columnFilterCriteria
        if (!filterCriteria.length) {
            return true
        }
        return filterCriteria.every(criterion =>
            this._dataItemMatchesColumnFilter(dataItem, criterion)
        )
    }

    _searchStringsForData(data: DataType[]): string[] {
        const descriptors = this._activeDescriptors()
        return data.map(dataItem => descriptors.map(descriptor => {
            if (descriptor.keyPath) {
                return dataItem[descriptor.keyPath as keyof DataType]
            }
            return ""
        }).join(" "))
    }

    _indexesOfStringsMatchingSearchText(strings: string[], searchText: string): number[] | Promise<number[]> {
        const filteringStrings = searchText.split(",").map(string => string.trim())
        const hasActiveFilter = filteringStrings.some(string => string.length > 0)
        // Skip the web worker entirely when there is nothing to filter —
        // serialising the full dataset via postMessage for a no-op is what
        // causes the multi-second freeze on initial tree load.
        if (!hasActiveFilter) {
            return strings.map((_string, index) => index)
        }
        else if (this.performsSearchFilteringSynchronously) {
            return filteringStrings.flatMap(filteringString => {
                const searchWords = filteringString
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(searchWord => searchWord.length > 0)
                const matchingIndexes: number[] = []
                strings.forEach((filteringValue, index) => {
                    const normalizedFilteringValue = filteringValue.toLowerCase()
                    if (searchWords.every(searchWord => normalizedFilteringValue.includes(searchWord))) {
                        matchingIndexes.push(index)
                    }
                })
                return matchingIndexes
            }).uniqueMap((index: number) => index)
        }
        else {
            return Promise.all(filteringStrings.map(filteringString =>
                this._stringFilter.filteredData(filteringString, strings)
            )).then(results => results.flatMap(result => result.filteredIndexes)
                .uniqueMap((index: number) => index))
        }

    }

    async dataByApplyingFiltersToData(data: DataType[]): Promise<DataType[]> {
        const filteredIndexes = await this._indexesOfStringsMatchingSearchText(
            this._searchStringsForData(data), this.searchTextField.text
        )
        return filteredIndexes.map(index => data[index]).filter(dataItem =>
            this._dataItemMatchesColumnFilters(dataItem)
        )
    }


    async updateTableDataByFiltering() {
        const filterUpdateSequence = ++this._filterUpdateSequence
        const searchText = this.searchTextField.text
        const filteringStrings = searchText.split(",").map(string => string.trim())
        const hasActiveFilter = filteringStrings.some(s => s.length > 0)
        this._publishCooperativeSortFilterRequestIfNeeded()
        
        if (this.rowsAreInDisplayOrder) {
            this.filteredData = this.data
            this._wireHeaderCheckboxes()
            this._updateHeaderCheckboxStates()
            return YES
        }
        
        const descriptor = this._descriptors.firstElement
        const parentIdentifierKeyPath = descriptor?.parentIdentifierKeyPath ?? "_parent"
        const identifierKeyPath = descriptor?.identifierKeyPath ?? "_id"
        let filteredIndexes = this._indexesOfStringsMatchingSearchText(this._filteringArray, searchText)
        if (filteredIndexes instanceof Promise) {
            filteredIndexes = await filteredIndexes
        }
        if (filterUpdateSequence !== this._filterUpdateSequence) {
            return NO
        }
        
        
        const filteringResult: CBDataViewRow<DataType>[] = []
        
        filteredIndexes.forEach(index => {
            const dataItem = this.data[index]
            if (this._dataItemMatchesColumnFilters(dataItem)) {
                filteringResult.push(rowStateOf(dataItem))
            }
        })
        
        if (this.isTreeView) {
            
            // Recompute _CB_DataView_IsExpanded for every node from first principles:
            //
            //   manual "closed"    → always collapsed (user explicitly suppressed search)
            //   manual "open"      → always expanded  (user explicitly opened)
            //   manual undefined   → expanded only if this node is an ancestor of a
            //                        search match (auto-expansion); collapsed otherwise
            //
            // This replaces the snapshot approach entirely — there is nothing to restore
            // because the manual intent is always stored separately on the node itself.
            
            // First pass: collect ancestor IDs of all search-matching nodes.
            const autoExpandIds = new Set<string>()
            if (hasActiveFilter) {
                const collectAncestors = (value: DataType) => {
                    const parentKey = UIObject.valueForKeyPath(parentIdentifierKeyPath, value) as string
                    if (IS(parentKey) && (parentKey + "") === parentKey && !autoExpandIds.has(parentKey)) {
                        autoExpandIds.add(parentKey)
                        const parent = this._dataByIdentifier.get(parentKey)
                        if (parent) {
                            collectAncestors(parent)
                        }
                    }
                }
                filteredIndexes.forEach(index => collectAncestors(this.data[index]))
            }
            
            // Second pass: derive _CB_DataView_IsExpanded for every node.
            this.data.forEach(item => {
                const rowState = rowStateOf(item)
                const manual = rowState._CB_DataView_ManualExpansion
                const id = UIObject.valueForKeyPath(identifierKeyPath, item) as string
                if (manual === "closed") {
                    rowState._CB_DataView_IsExpanded = false
                }
                else if (manual === "open") {
                    rowState._CB_DataView_IsExpanded = true
                }
                else {
                    // undefined — auto-expand only if it is an ancestor of a match
                    rowState._CB_DataView_IsExpanded = autoExpandIds.has(id)
                }
            })
            
            // Include ancestor objects that were not themselves in the filter results.
            const dataPointsToIncludeAsParentData = this.data.filter(
                value => !filteringResult.contains(value) &&
                    autoExpandIds.has(UIObject.valueForKeyPath(identifierKeyPath as string, value) as string)
            )
            dataPointsToIncludeAsParentData.forEach(value => filteringResult.push(value))
            
        }
        
        let sortedData: DataType[] = filteringResult
        if (this._descriptors.anyMatch(object => object.allowSorting ?? NO)) {
            
            // Sort the data
            sortedData = []
            const { sortedIndexes } = await this._stringSorter.sortedData(
                JSON.parse(JSON.stringify(filteringResult)),
                this.tableHeaderView.sortingInstructions
            )
            sortedIndexes.forEach(index => sortedData.push(filteringResult[index]))

            if (filterUpdateSequence !== this._filterUpdateSequence) {
                return NO
            }
            
        }
        
        let result: DataType[] = sortedData
        
        if (this.isTreeView) {
            
            result = []
            
            // Move sub elements under parent elements
            const parentDataObjects = sortedData.filter(value =>
                IS_NOT(UIObject.valueForKeyPath(parentIdentifierKeyPath as string, value))
            )
            const groupedSubRows = sortedData.groupedBy(value =>
                UIObject.valueForKeyPath(parentIdentifierKeyPath as string, value)
            )
            
            delete groupedSubRows.undefined
            delete groupedSubRows[""]
            
            const addToResult = (
                result: CBDataViewRow<DataType>[],
                parentDataObjects: DataType[],
                identifierKey: string | undefined,
                groupedSubRows: Record<string, DataType[]>,
                nestingDepth = 0
            ) => {
                
                parentDataObjects.forEach(dataObject => {
                    
                    const value = rowStateOf(dataObject)
                    result.push(value)
                    
                    value._CB_DataView_NestingDepth = nestingDepth
                    value._CB_DataView_updateRowWithNestingDepth = this.updateRowWithNestingDepth
                    
                    
                    if (identifierKey) {
                        const identifier = UIObject.valueForKeyPath(identifierKey, value)
                        const subRows = groupedSubRows[identifier]
                        if (subRows) {
                            
                            // This is needed to avoid showing sub rows of non-expanded rows
                            if (value._CB_DataView_IsExpandable && !value._CB_DataView_IsExpanded) {
                                return
                            }
                            
                            addToResult(result, subRows, identifierKey, groupedSubRows, nestingDepth + 1)
                            
                            if (!value._CB_DataView_IsExpandable) {
                                
                                value._CB_DataView_IsExpandable = YES
                                value._CB_DataView_IsExpanded = YES
                                
                            }
                            
                        }
                    }
                    
                })
                
            }
            
            addToResult(result, parentDataObjects, identifierKeyPath, groupedSubRows)
            
        }
        
        
        const rowToKeepStaticAfterUpdateYPositionInView = (this._rowToKeepStaticAfterUpdate?.frame.y ?? 0) - this.tableView.contentOffset.y
        const rowToKeepStaticAfterUpdateDataPoint = this._rowToKeepStaticAfterUpdate?.data
        
        const previousData = this.filteredData
        this.filteredData = result
        
        this._wireHeaderCheckboxes()
        this._updateHeaderCheckboxStates()
        
        if (this._rowToKeepStaticAfterUpdate) {
            
            this.tableView.layoutIfNeeded()
            
            const newRowIndex = rowToKeepStaticAfterUpdateDataPoint ? result.indexOf(rowToKeepStaticAfterUpdateDataPoint) : -1
            const newRowPosition = this.tableView._rowPositionWithIndex(newRowIndex)
            
            const newRowPositionInView = newRowPosition.topY - this.tableView.contentOffset.y
            
            const animationDuration = this.tableView.animationDuration
            this.tableView.animationDuration = 0
            this.tableView.contentOffset = this.tableView.contentOffset.pointByAddingY(
                newRowPositionInView - rowToKeepStaticAfterUpdateYPositionInView
            )
            this.tableView.animationDuration = animationDuration
            
            this._rowToKeepStaticAfterUpdate = undefined
            
        }
        
        
        if (this._highlightNextFilterChange) {
            this.tableView.setNeedsLayout()
            this.tableView.highlightChanges(previousData, this.filteredData)
            this._highlightNextFilterChange = NO
        }

        return YES
        
    }
    
    
    override boundsDidChange(bounds: UIRectangle) {
        
        super.boundsDidChange(bounds)
        
        this._rowHeightCache.clear()
        this.tableView.invalidateSizeOfRowWithIndex(0)
        this.tableView.setNeedsLayoutUpToRootView()
        
    }
    
    
    async sortTableData() {
        
        await this.updateTableDataByFiltering()
        
    }
    
    
    // -------------------------------------------------------------------------
    // Selection column helpers
    // -------------------------------------------------------------------------
    
    /**
     * Returns the selected items for a given selection column identifier.
     * Includes the live row view if the row is currently visible.
     */
    selectionForIdentifier(identifier: string): { dataItem: DataType; rowView?: CBTableRowView<DataType> }[] {
        const tokens = CBDataView._selectionStore.get(identifier)
        if (!tokens || tokens.size === 0) {
            return []
        }
        const result: { dataItem: DataType; rowView?: CBTableRowView<DataType> }[] = []
        for (const item of this._data) {
            const token = CBDataView._itemTokens.get(item as object)
            if (token && tokens.has(token)) {
                result.push({ dataItem: item, rowView: this._rowViewForItem(item) })
            }
        }
        return result
    }
    
    
    /** Finds the currently visible row view bound to a given data item, if any. */
    private _rowViewForItem(item: DataType): CBTableRowView<DataType> | undefined {
        const visibleRows = this.tableView._visibleRows as CBTableRowView<DataType>[]
        return visibleRows.find(row => row.data === item)
    }
    
    
    /**
     * Expands a mixed CBColumnDescriptor array into the flat CBDataViewCellDescriptor
     * array that CBTableRowView and CBTableHeaderView consume.
     * Each CBSelectionColumnDescriptor becomes a single custom-view descriptor.
     */
    private _expandedCellDescriptors(
        columnDescriptors: CBColumnDescriptor<DataType>[]
    ): CBDataViewCellDescriptor<DataType>[] {
        return columnDescriptors.map(descriptor => {
            if (isCBSelectionColumnDescriptor(descriptor)) {
                return this._syntheticDescriptorForSelection(descriptor)
            }
            return this._descriptorWithNormalizedAlignment(descriptor as CBDataViewCellDescriptor<DataType>)
        })
    }


    private _descriptorWithNormalizedAlignment(
        descriptor: CBDataViewCellDescriptor<DataType>
    ): CBDataViewCellDescriptor<DataType> {
        if (!descriptor.alignment) {
            return descriptor
        }
        return {
            ...descriptor,
            titleLabelAlignment: descriptor.titleLabelAlignment ?? descriptor.alignment,
            cellLabelAlignment: descriptor.cellLabelAlignment ?? descriptor.alignment
        }
    }


    private _activeDescriptors(): CBDataViewCellDescriptor<DataType>[] {
        return this._descriptors.filter(descriptor => !descriptor.hidden)
    }


    private _layoutParametersForActiveDescriptors(): { cellWeights: number[], cellWidths: number[] } {
        const activeDescriptors = this._activeDescriptors()
        return {
            cellWeights: activeDescriptors.map(descriptor => descriptor.weight ?? 1),
            cellWidths: activeDescriptors.map(descriptor =>
                descriptor.absoluteWidth === undefined ? nil : descriptor.absoluteWidth
            ) as number[]
        }
    }


    private _applyDescriptorLayoutParameters(layoutParameters: { cellWeights: number[], cellWidths: number[] }) {
        this.tableHeaderView.cellWeights = layoutParameters.cellWeights
        this.tableHeaderView.cellWidths = layoutParameters.cellWidths
        this.tableView.cellWeights = layoutParameters.cellWeights
        this.tableView.cellWidths = layoutParameters.cellWidths

        ;(this.tableView._visibleRows as CBTableRowView<DataType>[]).forEach(row => {
            row.cellWeights = layoutParameters.cellWeights
            row.cellWidths = layoutParameters.cellWidths
            row.layoutParametersDidChange()
        })
        this._rowHeightCache.clear()
        this.tableView.invalidateSizeOfRowWithIndex(0)
        this.tableView.setNeedsLayout()
    }
    
    
    /**
     * Builds a synthetic CBDataViewCellDescriptor that renders a checkbox for the
     * given CBSelectionColumnDescriptor. CBDataView owns all toggle wiring;
     * the caller's initCell/updateCell hooks are called on top for appearance only.
     */
    private _syntheticDescriptorForSelection(
        selectionDescriptor: CBSelectionColumnDescriptor<DataType>
    ): CBDataViewCellDescriptor<DataType> {
        const multipleSelection = selectionDescriptor.selectionMode !== "single"
        const treeSelection = selectionDescriptor.treeSelection ?? "independent"
        const selectionColumnTitle = IF(selectionDescriptor.masterSelectionEnabled === NO)(() =>
            selectionDescriptor.title || ""
        ).ELSE(() => "")
        
        return {
            keyPath: "",
            allowSorting: false,
            alignment: UITextView.textAlignment.center,
            // Column layout lives on the descriptor — carry the selection descriptor's
            // layout fields over so they reach the derived cellWeights/cellWidths.
            weight: selectionDescriptor.weight,
            absoluteWidth: selectionDescriptor.absoluteWidth,
            minimumWidth: selectionDescriptor.minimumWidth,
            // Keep the generated descriptor title blank when the header checkbox is
            // visible: the header checkbox's own titleLabel carries the visible title.
            // Without a master checkbox, keep the descriptor title so the column is labeled.
            title: selectionColumnTitle,
            initCustomView: () => {
                if (selectionDescriptor.initCustomView) {
                    return selectionDescriptor.initCustomView()
                }
                const CheckboxClass = this.selectionCheckboxClass ?? CBDataView.defaultSelectionCheckboxClass
                return new CheckboxClass()
            },
            initCell: (cellView: CBCellView<DataType, UIView>) => {
                // Wire the toggle handler once during cell creation.
                const checkbox = cellView.customView as CBCheckbox
                checkbox.addTargetForControlEvent(CBCheckbox.controlEvent.SelectionChange, () => {
                    const dataItem = cellView._data as DataType
                    if (!dataItem) {
                        return
                    }
                    const isEnabled = IF(selectionDescriptor.enabled)(() =>
                        selectionDescriptor.enabled!(dataItem)
                    ).ELSE(() => YES)
                    if (!isEnabled) {
                        // A native/custom checkbox may have changed its visual state
                        // before this descriptor-level guard runs. Restore every
                        // visible checkbox from the authoritative selection store so
                        // a rejected interaction can never leave a false checkmark.
                        this._refreshVisibleCheckboxes(selectionDescriptor)
                        this._updateHeaderCheckboxStates()
                        return
                    }
                    let store = CBDataView._selectionStore.get(selectionDescriptor.identifier)
                    if (!store) {
                        store = new Set()
                        CBDataView._selectionStore.set(selectionDescriptor.identifier, store)
                    }
                    if (treeSelection !== "independent") {
                        this._toggleTreeSelection(dataItem, selectionDescriptor, store, checkbox.selected)
                    }
                    else {
                        const token = CBDataView._tokenForItem(dataItem as object)
                        if (checkbox.selected) {
                            if (!multipleSelection) {
                                // Deselect all others before selecting this one.
                                const previousTokens = Array.from(store)
                                store.clear()
                                for (const previousToken of previousTokens) {
                                    this._updateCheckboxForToken(previousToken, selectionDescriptor, NO)
                                }
                            }
                            store.add(token)
                        }
                        else {
                            store.delete(token)
                        }
                    }
                    // Write back to keyPath if specified.
                    if (selectionDescriptor.keyPath) {
                        UIObject.setValueForKeyPath(selectionDescriptor.keyPath, checkbox.selected, dataItem, YES)
                    }
                    this._refreshVisibleCheckboxes(selectionDescriptor)
                    this._updateHeaderCheckboxStates()
                    this.selectionDidChange?.(selectionDescriptor.identifier)
                })
                // Caller's initCell for appearance customisation.
                selectionDescriptor.initCell?.(cellView as CellView<DataType, CBCheckbox>)
            },
            updateCell: (cellView: CBCellView<DataType, UIView>, dataItem: DataType) => {
                // Store the data item on the cell so the toggle handler can read it.
                cellView._data = dataItem
                const checkbox = cellView.customView as CBCheckbox
                const token = CBDataView._tokenForItem(dataItem as object)
                const store = CBDataView._selectionStore.get(selectionDescriptor.identifier)
                checkbox.selected = store?.has(token) ?? NO
                checkbox.enabled = IF(selectionDescriptor.enabled)(() =>
                    selectionDescriptor.enabled!(dataItem)
                ).ELSE(() => YES)
                // Show indeterminate state on ancestor nodes when using tree selection.
                if (treeSelection !== "independent") {
                    const nodeId = this._nodeIdForItem(dataItem)
                    const ancestorIds = this._selectionAncestorIds.get(selectionDescriptor.identifier)
                    checkbox.indeterminate = !checkbox.selected && (ancestorIds?.has(nodeId) ?? false)
                }
                else {
                    checkbox.indeterminate = NO
                }
                if (selectionDescriptor.rowTitle) {
                    checkbox.titleLabel.text = selectionDescriptor.rowTitle(dataItem)
                }
                // Caller's updateCell for appearance customisation.
                selectionDescriptor.updateCell?.(cellView as CellView<DataType, CBCheckbox>, dataItem)
            }
        }
    }
    
    
    /**
     * Returns the identifier key path value for a data item, using the first
     * descriptor's identifierKeyPath (falling back to "_id").
     */
    private _nodeIdForItem(dataItem: DataType): string {
        const identifierKeyPath = this._descriptors.firstElement?.identifierKeyPath as string ?? "_id"
        return UIObject.valueForKeyPath(identifierKeyPath, dataItem) as string ?? ""
    }
    
    
    /**
     * Returns the parent identifier key path value for a data item, using the first
     * descriptor's parentIdentifierKeyPath (falling back to "_parent").
     */
    private _parentIdForItem(dataItem: DataType): string | undefined {
        const parentIdentifierKeyPath = this._descriptors.firstElement?.parentIdentifierKeyPath as string ?? "_parent"
        const value = UIObject.valueForKeyPath(parentIdentifierKeyPath, dataItem) as string | undefined
        return value || undefined
    }
    
    
    /**
     * Rebuilds `_selectionAncestorIds` for the given identifier by walking up the
     * parent chain from every selected node. Runs once per selection change —
     * O(depth) per selected node, O(1) per row render in updateCell.
     *
     * Must be called after any mutation to the selection store for this identifier,
     * and after `data =` assignment when treeSelection is not "independent".
     */
    private _rebuildSelectionAncestorIds(identifier: string) {
        const ancestorIds = new Set<string>()
        const store = CBDataView._selectionStore.get(identifier)
        if (!store || store.size === 0) {
            this._selectionAncestorIds.set(identifier, ancestorIds)
            return
        }
        // Build a reverse map from token → data item for O(1) lookups.
        const itemByToken = new Map<string, DataType>()
        for (const item of this._data) {
            const token = CBDataView._itemTokens.get(item as object)
            if (token) {
                itemByToken.set(token, item)
            }
        }
        // Build id → item map for parent lookups.
        const identifierKeyPath = this._descriptors.firstElement?.identifierKeyPath as string ?? "_id"
        const dataById = new Map<string, DataType>(
            this._data.map(
                item => [UIObject.valueForKeyPath(identifierKeyPath, item) as string, item] as [string, DataType])
        )
        // Walk ancestor chains for every selected token.
        for (const token of store) {
            const selectedItem = itemByToken.get(token)
            if (!selectedItem) {
                continue
            }
            let current: DataType | undefined = selectedItem
            while (current) {
                const parentId = this._parentIdForItem(current)
                if (!parentId) {
                    break
                }
                if (ancestorIds.has(parentId)) {
                    // Already walked this branch from another selected node.
                    break
                }
                ancestorIds.add(parentId)
                current = dataById.get(parentId)
            }
        }
        this._selectionAncestorIds.set(identifier, ancestorIds)
    }
    
    
    /**
     * Collects all descendant data items of the given node (not including the node
     * itself). Uses the flat `_data` array and the parent identifier key path.
     */
    private _descendantsOfItem(dataItem: DataType): DataType[] {
        const nodeId = this._nodeIdForItem(dataItem)
        const identifierKeyPath = this._descriptors.firstElement?.identifierKeyPath as string ?? "_id"
        const parentIdentifierKeyPath = this._descriptors.firstElement?.parentIdentifierKeyPath as string ?? "_parent"
        
        // Build a children map: parentId → children[]
        const childrenByParentId = new Map<string, DataType[]>()
        for (const item of this._data) {
            const parentId = UIObject.valueForKeyPath(parentIdentifierKeyPath, item) as string | undefined
            if (parentId) {
                const siblings = childrenByParentId.get(parentId) ?? []
                siblings.push(item)
                childrenByParentId.set(parentId, siblings)
            }
        }
        
        const result: DataType[] = []
        const queue: string[] = [nodeId]
        while (queue.length > 0) {
            const currentId = queue.shift()!
            const children = childrenByParentId.get(currentId) ?? []
            for (const child of children) {
                result.push(child)
                queue.push(UIObject.valueForKeyPath(identifierKeyPath, child) as string)
            }
        }
        return result
    }
    
    
    /**
     * Performs a tree-aware toggle on the selection store for the given data item.
     * Called from the checkbox SelectionChange handler when treeSelection is not
     * "independent". Mutates the store in place and rebuilds ancestor IDs.
     *
     * Behaviour per treeSelection mode — see CBSelectionColumnDescriptor.treeSelection
     * for the full contract:
     *
     * "single":
     *   - Checking: add node, remove all descendants.
     *   - Unchecking directly-selected node: remove it.
     *   - Unchecking indeterminate node (has selected descendants): remove all descendants.
     *
     * "cascadeDown":
     *   - Checking: add node and all descendants.
     *   - Unchecking: remove node and all descendants.
     *
     * "cascadeUp":
     *   - Checking: add node; then walk up — if all siblings are selected, select
     *     parent too, recursively.
     *   - Unchecking: remove node and all ancestors.
     */
    private _toggleTreeSelection(
        dataItem: DataType,
        selDesc: CBSelectionColumnDescriptor<DataType>,
        store: Set<string>,
        nowSelected: boolean
    ) {
        const treeSelection = selDesc.treeSelection ?? "independent"
        const token = CBDataView._tokenForItem(dataItem as object)
        const identifierKeyPath = this._descriptors.firstElement?.identifierKeyPath as string ?? "_id"
        const parentIdentifierKeyPath = this._descriptors.firstElement?.parentIdentifierKeyPath as string ?? "_parent"
        
        if (treeSelection === "single") {
            if (nowSelected) {
                // Select this node and clear all its descendants.
                store.add(token)
                const descendants = this._descendantsOfItem(dataItem)
                for (const descendant of descendants) {
                    store.delete(CBDataView._tokenForItem(descendant as object))
                }
            }
            else {
                const ancestorIds = this._selectionAncestorIds.get(selDesc.identifier)
                const nodeId = this._nodeIdForItem(dataItem)
                const isIndeterminate = !store.has(token) && (ancestorIds?.has(nodeId) ?? false)
                if (isIndeterminate) {
                    // Uncheck on an indeterminate node — clear all descendants.
                    const descendants = this._descendantsOfItem(dataItem)
                    for (const descendant of descendants) {
                        store.delete(CBDataView._tokenForItem(descendant as object))
                    }
                }
                else {
                    store.delete(token)
                }
            }
        }
        else if (treeSelection === "cascadeDown") {
            const descendants = this._descendantsOfItem(dataItem)
            if (nowSelected) {
                store.add(token)
                for (const descendant of descendants) {
                    store.add(CBDataView._tokenForItem(descendant as object))
                }
            }
            else {
                store.delete(token)
                for (const descendant of descendants) {
                    store.delete(CBDataView._tokenForItem(descendant as object))
                }
            }
        }
        else if (treeSelection === "cascadeUp") {
            const dataById = new Map<string, DataType>(
                this._data.map(
                    item => [UIObject.valueForKeyPath(identifierKeyPath, item) as string, item] as [string, DataType])
            )
            const childrenByParentId = new Map<string, DataType[]>()
            for (const item of this._data) {
                const parentId = UIObject.valueForKeyPath(parentIdentifierKeyPath, item) as string | undefined
                if (parentId) {
                    const siblings = childrenByParentId.get(parentId) ?? []
                    siblings.push(item)
                    childrenByParentId.set(parentId, siblings)
                }
            }
            if (nowSelected) {
                store.add(token)
                // Walk up — if all siblings of a node are now selected, select the parent too.
                let currentParentId = this._parentIdForItem(dataItem)
                while (currentParentId) {
                    const parent = dataById.get(currentParentId)
                    if (!parent) {
                        break
                    }
                    const siblings = childrenByParentId.get(currentParentId) ?? []
                    const allSiblingsSelected = siblings.every(
                        sibling => store.has(CBDataView._tokenForItem(sibling as object))
                    )
                    if (!allSiblingsSelected) {
                        break
                    }
                    store.add(CBDataView._tokenForItem(parent as object))
                    currentParentId = this._parentIdForItem(parent)
                }
            }
            else {
                store.delete(token)
                // Remove all ancestors — parent cannot remain selected if a child is deselected.
                let currentParentId = this._parentIdForItem(dataItem)
                while (currentParentId) {
                    const parent = dataById.get(currentParentId)
                    if (!parent) {
                        break
                    }
                    store.delete(CBDataView._tokenForItem(parent as object))
                    currentParentId = this._parentIdForItem(parent)
                }
            }
        }
        
        this._rebuildSelectionAncestorIds(selDesc.identifier)
    }
    
    
    /**
     * Pushes the current store state to all visible row checkboxes for the given
     * selection descriptor. Used after tree-aware toggles, which may change the
     * selection state of rows other than the one that was clicked.
     */
    private _refreshVisibleCheckboxes(selDesc: CBSelectionColumnDescriptor<DataType>) {
        const fullColumnIndex = this._columnDescriptors.findIndex(
            d => isCBSelectionColumnDescriptor(d) && d.identifier === selDesc.identifier
        )
        const columnIndex = this._visibleColumnIndexForFullDescriptorIndex(fullColumnIndex)
        if (columnIndex < 0) {
            return
        }
        const store = CBDataView._selectionStore.get(selDesc.identifier)
        const ancestorIds = this._selectionAncestorIds.get(selDesc.identifier)
        const treeSelection = selDesc.treeSelection ?? "independent"
        const identifierKeyPath = this._descriptors.firstElement?.identifierKeyPath as string ?? "_id"
        const visibleRows = this.tableView._visibleRows as CBTableRowView<DataType>[]
        for (const row of visibleRows) {
            const item = row.data as DataType
            if (!item) {
                continue
            }
            const rowToken = CBDataView._itemTokens.get(item as object)
            const isSelected = rowToken ? (store?.has(rowToken) ?? false) : false
            const cellView = row.dataCells[columnIndex]
            if (!cellView?.customView) {
                continue
            }
            const checkbox = cellView.customView as CBCheckbox
            checkbox.selected = isSelected
            if (treeSelection !== "independent") {
                const nodeId = UIObject.valueForKeyPath(identifierKeyPath, item) as string ?? ""
                checkbox.indeterminate = !isSelected && (ancestorIds?.has(nodeId) ?? false)
            }
            else {
                checkbox.indeterminate = NO
            }
        }
    }
    
    
    /**
     * Visually updates the checkbox in the row currently bound to the given token.
     * Used when deselecting all in single-selection mode.
     */
    private _updateCheckboxForToken(
        token: string,
        selDesc: CBSelectionColumnDescriptor<DataType>,
        selected: boolean
    ) {
        const fullColumnIndex = this._columnDescriptors.findIndex(
            d => isCBSelectionColumnDescriptor(d) && d.identifier === selDesc.identifier
        )
        const columnIndex = this._visibleColumnIndexForFullDescriptorIndex(fullColumnIndex)
        if (columnIndex < 0) {
            return
        }
        const visibleRows = this.tableView._visibleRows as CBTableRowView<DataType>[]
        for (const row of visibleRows) {
            const item = row.data
            if (!item) {
                continue
            }
            const rowToken = CBDataView._itemTokens.get(item as object)
            if (rowToken === token) {
                const cellView = row.dataCells[columnIndex]
                if (cellView?.customView) {
                    (cellView.customView as CBCheckbox).selected = selected
                }
                break
            }
        }
    }
    
    
    /** Produces the map from column index → CBSelectionColumnDescriptor for the header. */
    private _selectionColumnIndicesMap(
        columnDescriptors: CBColumnDescriptor<DataType>[]
    ): Map<number, CBSelectionColumnDescriptor<DataType>> {
        const result = new Map<number, CBSelectionColumnDescriptor<DataType>>()
        columnDescriptors.forEach((descriptor, index) => {
            if (isCBSelectionColumnDescriptor(descriptor)) {
                const visibleIndex = this._visibleColumnIndexForFullDescriptorIndex(index)
                if (visibleIndex >= 0) {
                    result.set(visibleIndex, descriptor as CBSelectionColumnDescriptor<DataType>)
                }
            }
        })
        return result
    }


    private _visibleColumnIndexForFullDescriptorIndex(fullIndex: number): number {
        if (fullIndex < 0 || fullIndex >= this._descriptors.length || this._descriptors[fullIndex]?.hidden) {
            return -1
        }
        let visibleIndex = -1
        for (let index = 0; index <= fullIndex; index++) {
            if (!this._descriptors[index]?.hidden) {
                visibleIndex++
            }
        }
        return visibleIndex
    }
    
    
    /**
     * Updates all header checkboxes to reflect the current selection state
     * of the visible rows (checked / indeterminate / unchecked).
     */
    _updateHeaderCheckboxStates() {
        const selectionDescriptors = this._columnDescriptors
            .map((d, i) => ({ descriptor: d, index: i }))
            .filter(({ descriptor }) => isCBSelectionColumnDescriptor(descriptor))
        
        for (const { descriptor, index } of selectionDescriptors) {
            const visibleIndex = this._visibleColumnIndexForFullDescriptorIndex(index)
            if (visibleIndex < 0) {
                continue
            }
            const selDesc = descriptor as CBSelectionColumnDescriptor<DataType>
            if (selDesc.masterSelectionEnabled === false) {
                continue
            }
            const headerCheckbox = this.tableHeaderView.selectionColumnIndices?.get(visibleIndex)?._headerCheckbox
            if (!headerCheckbox) {
                continue
            }
            const store = CBDataView._selectionStore.get(selDesc.identifier)
            const enabledItems = this._filteredData.filter((item: DataType) =>
                selDesc.enabled ? selDesc.enabled(item) : YES
            )
            if (enabledItems.length === 0) {
                headerCheckbox.selected = NO
                headerCheckbox.indeterminate = NO
                continue
            }
            const selectedCount = enabledItems.filter((item: DataType) => {
                const token = CBDataView._itemTokens.get(item as object)
                return token ? (store?.has(token) ?? NO) : NO
            }).length
            if (selectedCount === 0) {
                headerCheckbox.selected = NO
                headerCheckbox.indeterminate = NO
            }
            else if (selectedCount === enabledItems.length) {
                headerCheckbox.selected = YES
                headerCheckbox.indeterminate = NO
            }
            else {
                headerCheckbox.selected = NO
                headerCheckbox.indeterminate = YES
            }
        }
    }
    
    
    /**
     * Wires master header checkboxes after the header is (re)assigned.
     * Called from the constructor and from RHDataView after header replacement.
     */
    _wireHeaderCheckboxes() {
        const selectionDescriptors = this._columnDescriptors
            .map((d, i) => ({ descriptor: d, index: i }))
            .filter(({ descriptor }) => isCBSelectionColumnDescriptor(descriptor))
        
        for (const { descriptor, index } of selectionDescriptors) {
            const visibleIndex = this._visibleColumnIndexForFullDescriptorIndex(index)
            if (visibleIndex < 0) {
                continue
            }
            const selDesc = descriptor as CBSelectionColumnDescriptor<DataType>
            if (selDesc.masterSelectionEnabled === false) {
                continue
            }
            const entry = this.tableHeaderView.selectionColumnIndices?.get(visibleIndex)
            if (!entry) {
                continue
            }
            if (entry._headerCheckbox) {
                continue
            }
            const CheckboxClass = this.selectionCheckboxClass ?? CBDataView.defaultSelectionCheckboxClass
            const headerCheckbox = new CheckboxClass()
            if (selDesc.title) {
                headerCheckbox.titleLabel.text = selDesc.title
            }
            entry._headerCheckbox = headerCheckbox
            this.tableHeaderView.addSubview(headerCheckbox)
            
            headerCheckbox.addTargetForControlEvent(CBCheckbox.controlEvent.SelectionChange, () => {
                const enabledItems = this._filteredData.filter((item: DataType) =>
                    selDesc.enabled ? selDesc.enabled(item) : YES
                )
                let store = CBDataView._selectionStore.get(selDesc.identifier)
                if (!store) {
                    store = new Set()
                    CBDataView._selectionStore.set(selDesc.identifier, store)
                }
                if (headerCheckbox.selected) {
                    for (const item of enabledItems) {
                        store.add(CBDataView._tokenForItem(item as object))
                    }
                }
                else {
                    for (const item of enabledItems) {
                        store.delete(CBDataView._tokenForItem(item as object))
                    }
                }
                // Write back keyPath if specified
                if (selDesc.keyPath) {
                    for (const item of enabledItems) {
                        UIObject.setValueForKeyPath(selDesc.keyPath, headerCheckbox.selected, item, YES)
                    }
                }
                // Refresh all visible row checkboxes directly — reloadData() does not
                // force-rebind rows that are already visible, so we push the new
                // selected state to each visible row's selection cell ourselves.
                const visibleRows = this.tableView._visibleRows as CBTableRowView<DataType>[]
                for (const row of visibleRows) {
                    const item = row.data as DataType
                    if (!item) {
                        continue
                    }
                    const rowToken = CBDataView._itemTokens.get(item as object)
                    const isEnabled = selDesc.enabled ? selDesc.enabled(item) : YES
                    const isSelected = rowToken ? (store.has(rowToken) && isEnabled) : NO
                    const cellView = row.dataCells[visibleIndex]
                    if (cellView?.customView) {
                        (cellView.customView as CBCheckbox).selected = isSelected
                    }
                }
                this._updateHeaderCheckboxStates()
                this.selectionDidChange?.(selDesc.identifier)
            })
        }
    }
    
    get hasResizableColumns(): boolean {
        return this._hasResizableColumns
    }
    
    set hasResizableColumns(hasResizableColumns: boolean) {
        
        this._hasResizableColumns = hasResizableColumns
        
        this._resizingHandleViews.everyElement.removeFromSuperview()
        this._resizingHandleViews = []
        
        if (!this._hasResizableColumns) {
            return
        }
        
        for (let i = 1; i < this.tableHeaderView.cells.length - 2; i++) {
            
            const view = new UIView().configuredWithObject({
                //backgroundColor: UIColor.blackColor,
                style: {
                    cursor: "col-resize"
                },
                hoverText: "Resize columns",
                calculateAndSetViewFrame: () => {
                    view.frame = this.tableHeaderView.cells[i].frame.rectangleWithWidth(2, 1)
                        .rectangleByAddingX(1)
                        .rectangleWithHeight(this.tableView.frame.height + this.tableHeaderView.frame.height)
                        .rectangleWithY(this.tableHeaderView.frame.y)
                }
            })
            
            // noinspection JSMismatchedCollectionQueryUpdate
            let initialWeights: number[] = []
            let initialUserSelect = ""
            let minWidths: number[] = []
            
            // noinspection JSConstantReassignment
            view.controlEventTargetAccumulator.PointerDown = () => {
                
                initialWeights = this.tableHeaderView.cells.map(cell => cell.frame.width)
                
                initialUserSelect = this.rootView.style.userSelect
                this.rootView.style.userSelect = "none"
                
                view.pointerDraggingPoint.x = 0
                view.pointerDraggingPoint.y = 0
                
                minWidths = this.minWidths
                
            }
            
            // noinspection JSConstantReassignment
            view.controlEventTargetAccumulator.PointerDrag = sender => {
                
                // Get the x movement value
                const movementX = sender.pointerDraggingPoint?.x ?? 0
                
                const weights = this.cellWeightsWhenResizingWithParameters(initialWeights, i, minWidths, movementX)
                this.tableView.cellWeights = weights
                this.tableHeaderView.cellWeights = weights
                
                this.userRequestedWeights = weights.copy();
                
                (this.tableView._visibleRows as RowView[]).everyElement.layoutParametersDidChange()
                this.setNeedsLayout()
                
            }
            
            // noinspection JSConstantReassignment
            view.controlEventTargetAccumulator.PointerUp.PointerCancel = () => {
                this.rootView.style.userSelect = initialUserSelect
            }
            
            this._resizingHandleViews.push(view)
            
        }
        
        this.addSubviews(this._resizingHandleViews)
        
    }
    
    get userRequestedWeights(): number[] {
        
        this._userRequestedWeights = this._userRequestedWeights ?? JSON.parse(localStorage.getItem(
            "userRequestedWeights__" + this.superview?.class.name + "_" + this.class.name + "_" + this.propertyDescriptors.firstElement.name
        ) ?? "[]")
        
        return this._userRequestedWeights!
        
    }
    
    set userRequestedWeights(weights: number[]) {
        
        localStorage.setItem(
            "userRequestedWeights__" + this.superview?.class.name + "_" + this.class.name + "_" + this.propertyDescriptors.firstElement.name,
            JSON.stringify(weights ?? [])
        )
        
        this._userRequestedWeights = weights
        
    }
    
    get minWidths() {
        const activeDescriptors = this._activeDescriptors()
        return this.tableHeaderView.cells.map((cell, index) =>
            this._minimumWidthForColumn(cell, activeDescriptors[index])
        )
    }

    private _minimumWidthForColumn(
        cell: CellView,
        descriptor?: CBDataViewCellDescriptor<DataType>
    ): number {
        if (descriptor?.absoluteWidth !== undefined) {
            return descriptor.absoluteWidth
        }

        const headerContentWidth = this._minimumHeaderWidthForColumn(cell, descriptor)
        const customContentWidth = descriptor?.initCustomView ? 96 : 0

        return [
            57,
            descriptor?.minimumWidth ?? 0,
            headerContentWidth,
            customContentWidth
        ].max()
    }

    private _minimumHeaderWidthForColumn(
        cell: CellView,
        descriptor?: CBDataViewCellDescriptor<DataType>
    ): number {
        const titleLabel = cell.titleLabel
        const headerText = this._plainHeaderText(titleLabel?.text ?? "")
        let measuredHeaderTextWidth = titleLabel?.intrinsicContentWidth(
            this.tableHeaderView.frame.height || this.tableHeaderView.intrinsicContentHeight()
        ) ?? 0
        if (titleLabel) {
            const measurementStyles = UITextMeasurement.elementStylesForElement(
                titleLabel.textElementView.viewHTMLElement
            )
            const textFragments: string[] = IF(titleLabel.isSingleLine)(() => [headerText])
                .ELSE(() => headerText.split(/\s+/))
            const fragmentWidths = textFragments.map(fragment => UITextMeasurement.measureTextWidth(
                fragment,
                measurementStyles.font,
                measurementStyles.letterSpacing
            ))
            if (fragmentWidths.every(Number.isFinite)) {
                measuredHeaderTextWidth = fragmentWidths.max()
            }
        }
        const headerTextWidth = Math.ceil(measuredHeaderTextWidth)

        const hasSortControl = !!(descriptor?.allowSorting && descriptor?.keyPath)
        const hasFilterControl = !!descriptor?.filter
        const sortControlWidth = IF(hasSortControl)(() => cell.sizeOfRightImage + cell.gapBetweenTitleAndRightImage)
            .ELSE(() => 0)
        const filterControlWidth = hasFilterControl ? kHeaderFilterControlAllowance : 0
        const rightControlWidth =
            sortControlWidth +
            filterControlWidth
        const centersHeaderText =
            !descriptor?.titleLabelAlignment ||
            descriptor.titleLabelAlignment === UITextView.textAlignment.center
        const leftControlWidth = IF(centersHeaderText)(() => rightControlWidth).ELSE(() => 0)
        const buttonPadding = cell.buttonView ? cell.buttonView.contentPadding * 2 : 0

        return (
            headerTextWidth +
            cell.contentInsets.left +
            cell.contentInsets.right +
            leftControlWidth +
            rightControlWidth +
            buttonPadding
        )
    }

    private _plainHeaderText(text: string): string {
        return text
            .replace(/<[^>]*>/g, " ")
            .replace(/&nbsp;/g, " ")
            .replace(/\s+/g, " ")
            .trim()
    }

    private _arraysAreEqual(first: number[], second: number[]): boolean {
        if (first.length !== second.length) {
            return NO
        }
        return first.every((value, index) => Math.abs(value - second[index]) < 0.5)
    }

    protected _applyAutomaticColumnSizing() {
        if (!this.usesAutomaticColumnSizing) {
            this._gridContainerView.minimumContentWidth = 0
            this._gridContainerView.style.overflowX = "hidden"
            return
        }

        const minimumWidths = this.minWidths
        const columnCount = minimumWidths.length
        if (!columnCount) {
            this._gridContainerView.minimumContentWidth = 0
            this._gridContainerView.style.overflowX = "hidden"
            return
        }

        const minimumContentWidth =
            minimumWidths.summedValue +
            this.tableHeaderView.padding * Math.max(0, columnCount - 1) +
            this.tableHeaderView.sideCellsWidth * 2

        let rowMeasurementsDidChange = NO

        if (!this._arraysAreEqual(this.tableHeaderView.cellMinimumWidths, minimumWidths)) {
            this.tableHeaderView.cellMinimumWidths = minimumWidths
            rowMeasurementsDidChange = YES
        }
        if (!this._arraysAreEqual(this.tableView.cellMinimumWidths ?? [], minimumWidths)) {
            this.tableView.cellMinimumWidths = minimumWidths
            rowMeasurementsDidChange = YES
        }
        ;(this.tableView._visibleRows as CBTableRowView<DataType>[]).forEach(row => {
            if (!this._arraysAreEqual(row.cellMinimumWidths, minimumWidths)) {
                row.cellMinimumWidths = minimumWidths
                rowMeasurementsDidChange = YES
            }
        })

        const shouldScrollHorizontally = minimumContentWidth > this._gridContainerView.frame.width
        const overflowX = shouldScrollHorizontally ? "auto" : "hidden"
        const gridMinimumWidthDidChange =
            Math.abs(this._gridContainerView.minimumContentWidth - minimumContentWidth) >= 0.5
        const overflowDidChange = this._gridContainerView.style.overflowX !== overflowX

        if (gridMinimumWidthDidChange) {
            this._gridContainerView.minimumContentWidth = minimumContentWidth
        }
        if (overflowDidChange) {
            this._gridContainerView.style.overflowX = overflowX
        }
        if (gridMinimumWidthDidChange || overflowDidChange) {
            this._gridContainerView.setNeedsLayout()
        }
        if (rowMeasurementsDidChange) {
            this._rowHeightCache.clear()
            this.tableView.invalidateSizeOfRowWithIndex(0)
            this.tableView.setNeedsLayout()
        }
    }
    
    private cellWeightsWhenResizingWithParameters(
        initialWeights: number[],
        leftColumnIndex: number,
        minWidths: number[],
        movementX: number
    ) {
        // Change the cellWeights of the tableView
        
        const weights = initialWeights.copy()
        
        const leftWeight = initialWeights[leftColumnIndex]
        const rightWeight = initialWeights[leftColumnIndex + 1]
        
        const minLeftWeight = minWidths[leftColumnIndex]
        const minRightWeight = [minWidths[leftColumnIndex + 1], this.core.paddingLength * 2, 57].max()
        const maxLeftWeight = leftWeight + rightWeight - minRightWeight
        const maxRightWeight = leftWeight + rightWeight - minLeftWeight
        
        weights[leftColumnIndex] = (leftWeight + movementX).constrainedValue(minLeftWeight, maxLeftWeight)
        weights[leftColumnIndex + 1] = (rightWeight - movementX).constrainedValue(minRightWeight, maxRightWeight)
        
        return weights
        
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        
        // CRITICAL: Use contentBoundsWithInset instead of this.bounds
        // This ensures proper inset handling and intrinsic size calculations
        const bounds = this.contentBoundsWithInset(padding)
        
        this.titleLabel.frame = bounds.rectangleWithHeight(labelHeight)
        
        this.searchTextField.hidden = IS_NOT(this.isSearchAvailable)
        
        this.searchTextField.frame = this.titleLabel.frame.rectangleForNextRow(padding * 0.5)
            .rectangleWithHeight(padding * 1.5)
        
        const previousFrameFromTableHeader = IF(this.isSearchAvailable)(() => this.searchTextField.frame)
            .ELSE_IF(this.titleLabel.text)(() => this.titleLabel.frame)
            .ELSE(() => bounds.rectangleWithHeight(0))
        
        this._gridContainerView.frame = previousFrameFromTableHeader
            .IF(!this.isVirtualLayouting && bounds.height > 0)
            .rectangleForNextRow(
                padding,
                [0, bounds.height - previousFrameFromTableHeader.max.y - padding].max()
            )
            .ELSE()
            .rectangleForNextRow(padding, this._gridContainerView)
            .ENDIF()

        this._applyAutomaticColumnSizing()
        
        this._noDataMessageLabel.frame = this._gridContainerView.frame
            .rectangleForNextRow(padding, labelHeight)
        
        
        if (this.tableHeaderView.boundsHaveChangedSinceLayout && this.hasResizableColumns) {
            
            const minWidths = this.minWidths
            const weights = this.tableHeaderView.cellWeights.map(
                (weight, index) => [this.userRequestedWeights?.[index] ?? weight, weight].min()
            )
            
            const weightDescriptors: {
                currentWeight: number;
                index: number;
                widthWithCurrentWeight: number;
                minWidth: number,
                widthLeftAboveMin: number
                needsExpanding: boolean,
            }[] = []
            
            const availableTotalWidth = bounds.width - this.tableHeaderView.sideCellsWidth * 2
            
            for (let i = 1; i < this.tableHeaderView.cells.length - 1; i++) {
                
                const weight = weights[i]
                const minWidth = minWidths[i]
                
                const widthWithCurrentWeight = (weight / weights.summedValue) * availableTotalWidth
                
                const needsExpanding = widthWithCurrentWeight < minWidth
                
                weightDescriptors.push({
                    
                    index: i,
                    currentWeight: weight,
                    minWidth: minWidth,
                    widthWithCurrentWeight: widthWithCurrentWeight,
                    widthLeftAboveMin: widthWithCurrentWeight - minWidth,
                    needsExpanding: needsExpanding
                    
                })
                
                
            }
            
            const sortedWeightDescriptors = weightDescriptors.copy().sort((
                a,
                b
            ) => (a.widthLeftAboveMin) - (b.widthLeftAboveMin))
            
            const weightDescriptorsThatNeedExpanding = sortedWeightDescriptors.filter(
                descriptor => descriptor.needsExpanding
            )
            
            const weightDescriptorsThatHaveSpace = sortedWeightDescriptors.filter(
                descriptor => !descriptor.needsExpanding
            )
            
            const totalSpace = weightDescriptorsThatHaveSpace.everyElement.widthLeftAboveMin.UI_elementValues?.summedValue ?? 0
            
            const totalSpaceNeededForExpansion = weightDescriptorsThatNeedExpanding.map(
                descriptor => -descriptor.widthLeftAboveMin).summedValue
            
            weightDescriptorsThatNeedExpanding.forEach(descriptor => weights[descriptor.index] = descriptor.minWidth)
            weightDescriptorsThatHaveSpace.forEach(descriptor => {
                
                const spaceChangeMultiplier = descriptor.widthLeftAboveMin / totalSpace
                
                weights[descriptor.index] = descriptor.widthWithCurrentWeight - totalSpaceNeededForExpansion * spaceChangeMultiplier
                
            })
            
            
            this.tableHeaderView.cellWeights = weights
            this.tableView.cellWeights = weights
            
            this.tableView.setNeedsLayout()
            
            
        }
        
        
    }
    
    
    /**
     * Opt-in switch for `intrinsicContentHeight`'s row-measurement strategy - unset (the default)
     * costs nothing extra and keeps existing behavior byte-for-byte. Set it (typically via
     * `configuredWithObject({ maximumIntrinsicContentHeight: ... })` alongside the rest of a
     * dataView's setup) to make `intrinsicContentHeight` - and therefore every ordinary fluent
     * auto-sizing call site, e.g. `bounds.rectangleForNextRow(padding, this.dataView)` - report a
     * REAL, measured height instead of the cheap per-row estimate. See `intrinsicContentHeight`'s
     * own doc comment for why that estimate can be wrong, and what this property is for:
     *
     * - A finite number: "grow to fit real content, up to this many px, then scroll
     *   internally past that." The common case for a variable-height list that's usually short
     *   but could occasionally get long - cost stays bounded by this cap, not by row count.
     * - `Infinity`: "always report the true, real, uncapped height" - only for lists you know are
     *   short (cost scales with row count; each row measured can force a synchronous DOM reflow).
     */
    maximumIntrinsicContentHeight?: number


    /**
     * Returns the intrinsic content height of the data view.
     * This is called during virtual layouting where bounds.height = 0, so we
     * must use contentBoundsWithInset instead of direct bounds access.
     *
     * CAVEAT for `tableView.allRowsHaveEqualHeight === NO` (variable, content-dependent row
     * heights) when `maximumIntrinsicContentHeight` is NOT set: the row-height contribution is a
     * CHEAP ESTIMATE (`UITableView.intrinsicContentHeight`) for any row that has never actually
     * rendered inside the viewport - it falls back to a generic ~50px per row rather than that
     * row's real height. That's invisible for a short list (everything fits in the initial
     * viewport and gets measured for real) and silently under-reports as soon as the list has
     * more rows than that - exactly the "not tall enough past N rows" failure mode. This is a
     * real limitation, not a bug you can work around by calling this differently; pick one of
     * these instead when it applies to you:
     *
     * 1. **Fixed height + internal scroll** (simplest, always correct, and needs no measurement
     *    at all): give the dataView a hardcoded pixel height in the OWNING view's layout instead
     *    of sizing from `this.dataView`/`intrinsicContentHeight`. Sacrifices "grows to fit".
     * 2. **Manual `intrinsicContentHeight` override**, if you can derive row height as a closed
     *    form from your data without rendering anything (e.g. "always exactly 2 lines at a known
     *    line-height") - the cheapest and fastest option where it's actually valid, but wrong the
     *    moment that assumption stops holding.
     * 3. **Set `maximumIntrinsicContentHeight`** (see its own doc comment): real "grow to fit, up
     *    to a cap, then scroll" (a finite number) or real uncapped measurement (`Infinity`) -
     *    the usual choice for a variable-height list you want to auto-size normally.
     */
    override intrinsicContentHeight(constrainingWidth: number = 0): number {

        if (this.maximumIntrinsicContentHeight !== undefined) {
            return this.measuredIntrinsicContentHeight(constrainingWidth, this.maximumIntrinsicContentHeight).height
        }

        this._warnIfEstimatingUnboundedVariableRowHeight()

        return this._estimatedIntrinsicContentHeight(constrainingWidth)

    }


    // Fires the first time (per instance) this dataView's height is auto-sized from a cheap,
    // unreliable per-row estimate instead of a real measurement - see intrinsicContentHeight's
    // own doc comment for the full explanation. Dev-only: this is a footgun-detection aid for
    // whoever is writing the layout code, not something a production user should ever see.
    _warnIfEstimatingUnboundedVariableRowHeight() {
        if (this._hasWarnedAboutUnboundedRowHeightEstimate || this.tableView.allRowsHaveEqualHeight) {
            return
        }
        this._hasWarnedAboutUnboundedRowHeightEstimate = YES
        console.warn(
            `[CBDataView${this.elementID ? " " + this.elementID : ""}] intrinsicContentHeight() is auto-sizing ` +
            "this table (tableView.allRowsHaveEqualHeight is NO) using a cheap per-row ESTIMATE, not a real " +
            "measurement - rows that have never rendered fall back to ~50px regardless of their real height, " +
            "which silently under-reports as soon as the table has more rows than fit in whatever size it had " +
            "the first time it rendered. Pick one of: (1) fixed pixel height + internal scroll in the owning " +
            "view's layout, (2) allRowsHaveEqualHeight: YES if rows really are uniform, (3) a manual " +
            "intrinsicContentHeight override if row height is a closed form from your data, or (4, usually the " +
            "right one here) set maximumIntrinsicContentHeight - a number for \"grow to fit, capped\", or " +
            "Infinity for \"always real, uncapped\"."
        )
    }


    /** The original cheap-estimate computation, unconditionally - see `intrinsicContentHeight`'s
     *  doc comment. Kept separate from the public `intrinsicContentHeight` so
     *  `measuredIntrinsicContentHeight` can always reach the real chrome-height computation below
     *  without recursing back through whichever strategy `intrinsicContentHeight` is currently
     *  configured to use. */
    _estimatedIntrinsicContentHeight(constrainingWidth: number): number {

        const padding = this.core.paddingLength
        const labelHeight = padding * 2

        // The search-hidden layout starts the grid after two paddings and leaves two
        // paddings below it. Its title row contributes only when it has text.
        const gridChromeHeight = IF(this.isSearchAvailable)(() =>
            padding * 7.5
        ).ELSE(() =>
            padding * 4 + IF(this.titleLabel.text)(() => labelHeight).ELSE(() => 0)
        )

        // Calculate height without relying on bounds.height
        let result = gridChromeHeight +
            this._gridContainerView.intrinsicContentHeight(constrainingWidth)

        if (this.noDataMessage && !this.filteredData.length) {

            // Add one header's worth of height for the no data message label
            result = result + this.tableHeaderView.intrinsicContentHeight(constrainingWidth)

        }

        return result

    }


    /**
     * Like `intrinsicContentHeight`, but backed by REAL measured row heights
     * (`UITableView.measuredRowsHeight`) instead of the cheap per-row estimate - see
     * `intrinsicContentHeight`'s doc comment for why that matters. Normally reached indirectly by
     * setting `maximumIntrinsicContentHeight`; called directly here (and by
     * `intrinsicContentHeight` itself, once that property is set) - use it directly only if you
     * need `wasClamped`, which `intrinsicContentHeight`'s plain `number` return can't carry.
     *
     * Everything BUT the table's own row-height contribution (header, search accessory, padding,
     * "no data" message, ...) is derived by subtracting `tableView.intrinsicContentHeight()` back
     * out of the cheap-estimate total (`_estimatedIntrinsicContentHeight`), rather than
     * re-deriving that chrome by hand here - so this stays correct automatically if the
     * surrounding chrome layout ever changes, without needing to be kept in sync by hand.
     *
     * Pass `maximumHeight` for the "grow to fit, up to a cap, then scroll internally" pattern:
     * rows are measured for real, in order, and measurement stops the instant the running total
     * would exceed it - `wasClamped` comes back `true` and every remaining row is left
     * unmeasured. Cost is then proportional to however many rows it takes to fill the cap, not to
     * the total row count, so it stays cheap for long lists as long as the cap itself is modest.
     * Pass `Infinity` (or omit it) for the true full height - only for lists you know are short:
     * real measurement without a cap is O(row count) DOM layout work.
     */
    measuredIntrinsicContentHeight(constrainingWidth: number, maximumHeight?: number): {
        height: number
        wasClamped: boolean
    } {
        const measuredRowsTableView =
            this.tableView as UITableViewWithOptionalMeasuredRowsHeight

        if (this.noDataMessage && !this.filteredData.length) {
            return {
                height: this._estimatedIntrinsicContentHeight(constrainingWidth),
                wasClamped: NO
            }
        }

        if (typeof measuredRowsTableView.measuredRowsHeight !== "function") {
            const measuredHeight = this._estimatedIntrinsicContentHeight(constrainingWidth)
            if (
                maximumHeight !== undefined &&
                maximumHeight !== Infinity &&
                measuredHeight > maximumHeight
            ) {
                return {
                    height: maximumHeight,
                    wasClamped: YES
                }
            }
            return {
                height: measuredHeight,
                wasClamped: NO
            }
        }

        const chromeHeight =
            this._estimatedIntrinsicContentHeight(constrainingWidth) -
            this.tableView.intrinsicContentHeight(constrainingWidth)
        const rowBudget = maximumHeight !== undefined && maximumHeight !== Infinity
            ? Math.max(0, maximumHeight - chromeHeight)
            : undefined

        const { height: rowsHeight, wasClamped } =
            measuredRowsTableView.measuredRowsHeight(constrainingWidth, rowBudget)

        return {
            height: chromeHeight + rowsHeight,
            wasClamped
        }
    }


}
